// index.js
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import {
  CaretDownFill,
  CaretUpFill,
  ArrowCounterclockwise,
  ChevronDown,
  ArrowRight,
  Download,
} from "react-bootstrap-icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  ArcElement,
  PointElement,
  LineController,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import {
  errorsChartOptions,
  customMarginPlugin,
} from "./chartOptions";
import EmptyChart, {
  NoDataMessageContent,
} from "./EmptyChart";
import consentMonitoring from "./consentMonitoring";
import cloudRun from "./cloudRun";

// import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import DashboardService from "../../services/dashboard.service";
import { AuthContext } from "../../services/auth.service";

import PageHeader from "../../components/elements/PageHeader";
import LoadingScreen from "../../components/elements/LoadingScreen";
import Table from "../../components/elements/Table";
import Tabs from "../../components/elements/Tabs";
import BarChart from "../../components/elements/BarChart";
import HiddenColumnsTable from "../../components/elements/HiddenColumnsTable";
import TagDiagnosticsDrawer from "./TagDiagnosticsDrawer";

import localStorageService from "../../services/localStorage.service";
import { canAccessTagDiagnostics } from "../../utils/featureAccess";
import { getUrlDateRange } from "../../utils/dashboardDates";
import "./styles.scss";
import "../../styles.scss";
import {
  Dropdown,
  Empty,
  Select,
  Space,
  Input,
  Row,
  Col,
  Flex,
  Layout,
  DatePicker,
  Segmented,
  Tooltip as AntdTooltip,
  Table as AntdTable,
  Checkbox,
  Divider,
  Switch,
  Tag,
} from "antd";
import {
  StopOutlined,
  InfoCircleFilled,
  CloseOutlined,
  WarningFilled,
  CheckCircleFilled,
} from "@ant-design/icons";
import ExcelJS from "exceljs";
import LineChart from "./LineChart";

// Helper: convert 1-based column index to Excel column letters (1 -> A, 27 -> AA)
const colIndexToLetter = (index) => {
  let letters = "";
  let i = index;
  while (i > 0) {
    const mod = (i - 1) % 26;
    letters = String.fromCharCode(65 + mod) + letters;
    i = Math.floor((i - 1) / 26);
  }
  return letters;
};

// The anomaly model stores counts log1p-transformed; SQL already inverts them
// into actual_count / actual_yhat_lower / actual_yhat_upper, so JS only sums.

// Margin guard: the expected band is padded before a point counts as an anomaly,
// with the upper edge floored at 10 so low-volume tags never flag tiny spikes.
const MARGIN_PERCENT_UPPER = 1; // +100% above the expected upper
const MARGIN_PERCENT_LOWER = 0.5; // -50% below the expected lower
const MIN_MARGIN = 10;

const marginUpperEdge = (upper) =>
  Math.max(upper * (1 + MARGIN_PERCENT_UPPER), MIN_MARGIN);
const marginLowerEdge = (lower) =>
  Math.max(0, lower * (1 - MARGIN_PERCENT_LOWER));

// A row is a real anomaly only if the model flagged it (from the DB) AND the actual
// count falls outside the margin-padded band. The flag can be a spike (1) or a
// drop (-1); either counts. All inputs come from the DB row.
export const isRowAnomalous = (row) => {
  const flag = Number(row.anomaly);
  if (flag !== 1 && flag !== -1) return false;
  const count = Number(row.actual_count);
  const lower = Number(row.actual_yhat_lower);
  const upper = Number(row.actual_yhat_upper);
  // If the band is missing, fall back to the model's flag rather than guessing.
  if (Number.isNaN(count) || Number.isNaN(lower) || Number.isNaN(upper)) return true;
  return count > marginUpperEdge(upper) || count < marginLowerEdge(lower);
};

const { Content } = Layout;
const { RangePicker } = DatePicker;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  ArcElement,
  PointElement,
  LineController,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

export const getDateRangeCode = (startDateStr, endDateStr) => {
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);

  // Calculate the difference in milliseconds
  const differenceMs = Math.abs(endDate - startDate);

  // Number of milliseconds in a day
  const oneDayMs = 1000 * 60 * 60 * 24;

  // Number of milliseconds in an hour
  const oneHourMs = 1000 * 60 * 60;

  // Get the current date and time
  const now = new Date();

  // Calculate the difference between now and the end date
  const differenceFromNowMs = Math.abs(now - endDate);

  let result;

  // Check for 1 hour with tolerance (within 2 minutes to account for timing drift)
  if (Math.abs(differenceMs - oneHourMs) < 120000) {
    result = "last_hour";
  }
  // Check for 1 day
  else if (Math.abs(differenceMs - oneDayMs) < 120000) {
    if (differenceFromNowMs <= oneDayMs) {
      result = "last_24_hours";
    } else {
      result = "one_day";
    }
  }
  // Check if both dates are same moment (very small difference)
  else if (differenceMs < 60000) {
    if (startDate.toDateString() === now.toDateString()) {
      result = "today";
    } else {
      result = "one_day";
    }
  }
  // Check if both dates are today (but with time difference)
  else if (
    startDate.toDateString() === now.toDateString() &&
    endDate.toDateString() === now.toDateString()
  ) {
    // If difference is close to 1 hour, it's last_hour not today
    if (Math.abs(differenceMs - oneHourMs) < oneHourMs / 2) {
      result = "last_hour";
    } else {
      result = "today";
    }
  } else {
    result = null;
  }

  return result;
};

export const TagMonitorDash = () => {
  const navigate = useNavigate();

  // Define the compant from the context
  const { companyId, companyInfo } = useContext(AuthContext);
  const [companyID, setCompanyID] = useState(companyId);
  const [clientTimeStamp, setClientTimeStamp] = useState("Europe/Amsterdam");
  // Per-table collapsed state for the "Needs attention" bar; reset when fresh
  // anomaly data loads.
  const [attentionBarCollapsed, setAttentionBarCollapsed] = useState({
    client: false,
    server: false,
  });
  const [datasetExists, setDatasetExists] = useState(true);

  const tagMonitorServer = companyInfo?.tag_monitor_server;
  const tagMonitorSubscription = companyInfo?.tag_monitor_subscription_model?.toLowerCase();
  const hasAnomalyAccess = tagMonitorSubscription === 'premium' || tagMonitorSubscription === 'professional' || tagMonitorSubscription === 'advanced' || tagMonitorSubscription === 'enterprise';

  // Set the loading status for the loading spinner
  const [updateLoadFlag, setUpdateLoadFlag] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);

  // Filters data
  const [filtersType, setFiltersType] = useState("client");
  const [filtersClientSide, setFiltersClientSide] = useState([]);
  const [filtersServerSide, setFiltersServerSide] = useState([]);
  const [domainsConfigured, setDomainsConfigured] = useState(null);
  const [configuredDomains, setConfiguredDomains] = useState([]);

  const [activeTab, setActiveTab] = useState("error_monitoring");
  const [filtersData, setFiltersData] = useState([]);

  const [tagsList, setTagsList] = useState([]);
  const [tagsDropdownList, setTagsDropdownList] = useState([]);
  const [cidList, setCidList] = useState([]);
  const [eventNameList, setEventNameList] = useState([]);
  const [hostsList, setHostsList] = useState([]);

  const [selectedTags, setSelectedTags] = useState([]);
  const [chosenContainerId, setChosenContainerId] = useState([]);
  const [chosenEventName, setChosenEventName] = useState([]);
  const [chosenContainerVersion, setchosenContainerVersion] = useState([]);
  const [chosenDomain, setChosenDomain] = useState([]);
  const [searchUrlValue, setSearchUrlValue] = useState("");
  const noFiltersApplied =
    selectedTags.length === 0 &&
    chosenContainerId.length === 0 &&
    chosenEventName.length === 0 &&
    chosenDomain.length === 0 &&
    searchUrlValue.trim() === "";

  // Logo for exports
  const [logoBase64, setLogoBase64] = useState(null);
  const [logoAspectRatio, setLogoAspectRatio] = useState(1);

  const [currentSelectedTags, setCurrentSelectedTags] = useState([]);
  const [currentChosenContainerId, setCurrentChosenContainerId] = useState([]);
  const [currentChosenEventName, setCurrentChosenEventName] = useState([]);
  const [currentChosenDomain, setCurrentChosenDomain] = useState([]);
  const [currentSearchUrlValue, setCurrentSearchUrlValue] = useState("");

  const [filtersDisabled, setFiltersDisabled] = useState(true);
  const [tabType, setTabType] = useState("errors_client");

  const [excludedTagsClient, setExcludedTagsClient] = useState([]);
  const [excludedTagsServer, setExcludedTagsServer] = useState([]);

  const [showExcludedTags, setShowExcludedTags] = useState(() => {
    const stored = localStorage.getItem("tagmonitor_show_excluded_tags");
    return stored === "true";
  });

  // Refs to ensure filterExcludedFromData always reads the latest values,
  // even when called from stale async closures (e.g. updateCharts via getChartsData)
  const excludedTagsClientRef = useRef(excludedTagsClient);
  const excludedTagsServerRef = useRef(excludedTagsServer);
  const showExcludedTagsRef = useRef(showExcludedTags);
  excludedTagsClientRef.current = excludedTagsClient;
  excludedTagsServerRef.current = excludedTagsServer;
  showExcludedTagsRef.current = showExcludedTags;

  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
    const tabParam = searchParams.get("tab");

  const [monitoringView, setMonitoringView] = useState(viewParam || "client");

  // Load and scale logo for exports
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const MAX_WIDTH = 440;
      const scale = Math.min(1, MAX_WIDTH / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setLogoBase64(canvas.toDataURL("image/png"));
      setLogoAspectRatio(img.width / img.height);
    };
    img.src = `${process.env.PUBLIC_URL}/code-cube.io-marketing-observability-white-blue.png`;
  }, []);

    useEffect(() => {
        if (viewParam && (viewParam === "client" || viewParam === "server")) {
            setMonitoringView(viewParam);
            // Check if tab parameter is provided
            if (tabParam === "tag_volume" || tabParam === "anomaly_detection") {
                handleDashTypeChangeValue(viewParam, `tag_volume_${viewParam}`, "tag_volume");
            } else {
                handleDashTypeChangeValue(viewParam, `errors_${viewParam}`, "error_monitoring");
            }
        }
    }, [viewParam, tabParam]);

    // Runs after the effect above, so it turns anomalies back on after the tab change switched them off
    useEffect(() => {
        if (tabParam === "anomaly_detection" && hasAnomalyAccess) setAnomalyToggleOn(true);
    }, [viewParam, tabParam, hasAnomalyAccess]);

  // Check and mute tags excluded from monitoring (for render-time visual marking)
  const isTagExcluded = (tag) => {
    const { tag_name, tag_id } = tag;

    return (
      excludedTagsClient.includes(tag_name) ||
      excludedTagsClient.includes(tag_id) ||
      excludedTagsServer.includes(tag_name) ||
      excludedTagsServer.includes(tag_id)
    );
  };

  // Filter excluded tags from data arrays (uses refs for async safety)
  const filterExcludedFromData = (dataArr) => {
    if (!Array.isArray(dataArr) || showExcludedTagsRef.current) return dataArr;
    return dataArr.filter((item) => {
      const { tag_name, tag_id } = item;
      return !(
        excludedTagsClientRef.current.includes(tag_name) ||
        excludedTagsClientRef.current.includes(tag_id) ||
        excludedTagsServerRef.current.includes(tag_name) ||
        excludedTagsServerRef.current.includes(tag_id)
      );
    });
  };

  useEffect(() => {
    if (tagsList.length > 0) {
      const updatedTags = tagsList.map((tag) => ({
        ...tag,
        excluded: isTagExcluded(tag),
      }));
      setTagsList(updatedTags);

      const updatedTagOptions = updatedTags.map((tag, index) => ({
        label: (
          <span className={tag.excluded ? "excluded-tag" : ""}>
            {tag.tag_name || tag.tag_id}
            {tag.excluded && (
              <>
                <StopOutlined className="excluded-icon font-bold" />
                <span className="excluded-text">excluded</span>
              </>
            )}
          </span>
        ),
        value: tag.tag_name || tag.tag_id,
        className: tag.excluded ? "excluded-tag" : "",
        key: `${tag.tag_name || tag.tag_id}-${index}`,
      }));
      setTagOptions(updatedTagOptions);
    }
  }, [excludedTagsClient, excludedTagsServer]);

  const tabsInfo = {
    errors_client: {
      description: (
        <>
          This dashboard highlights errors in your client-side Tag Management
          container.
        </>
      ),
    },
    errors_server: {
      description:
        "Monitor your server-side tags with real-time data, regardless of your container's hosting location. This dashboard shows errors per tag, event, domain, or container in Google Tag Manager, helping you quickly address issues to ensure data quality and effective marketing performance.",
    },
    tag_volume_client: {
      description:
        "This dashboard monitors tag inactivity, alerting you to sudden drops in data collection. By using static rules and real-time insights, it helps you quickly identify and address issues, ensuring continuous data flow and preventing disruptions to your analytics and marketing activities.",
    },
    tag_volume_server: {
      description:
        "This dashboard monitors tag inactivity, alerting you to sudden drops in data collection. By using static rules and real-time insights, it helps you quickly identify and address issues, ensuring continuous data flow and preventing disruptions to your analytics and marketing activities.",
    },
    taganomalyclient: {
      description:
        "Intelligent anomaly detection powered by machine learning monitors your tag firing patterns in real-time. This dashboard identifies unusual spikes or drops in tag activity, helping you catch issues before they impact your data collection and analytics.",
    },
    taganomalyserver: {
      description:
        "Intelligent anomaly detection powered by machine learning monitors your server-side tag firing patterns in real-time. This dashboard identifies unusual spikes or drops in tag activity, helping you catch issues before they impact your data collection and analytics.",
    },
    site_performance: {
      description:
        "This dashboard tracks how tags impact site performance, offering insights on loading times and user experience to help you optimize tag implementation without compromising data collection or marketing.",
    },
    consent_monitoring: {
      description:
        "Track and manage consent settings with our dashboard to ensure accurate data and strong campaign results. It monitors consent statuses for all tags and triggers in Google Tag Manager, offering real-time alerts and insights to help you maintain data integrity and optimize marketing efforts.",
    },
    cloud_run: {
      description:
        "With Cloud Run Monitoring your server-side Cloud Run instances will be monitored in real-time. An essential part to monitor as your whole marketing- and analytics set up is dependent on the health of this server.",
    },
  };

  const tableInfo = {
    errors_client: {
      title: "Error Monitoring",
      description:
        "View the errors occurring with client-side tags. You can filter by tag, event, container, or domain.",
    },
    errors_server: {
      title: "Error Monitoring",
      description:
        "Monitor your server-side tags in real time, regardless of where your container is hosted. This overview shows errors per tag, event, domain, or container, enabling you to act quickly and safeguard both data quality and marketing performance.",
    },
    tag_volume_client: {
      title: "Tag Volume",
      description:
        "This table shows the activity of client-side tags. It helps you quickly detect sudden drops in data collection and take proactive measures to prevent disruptions.",
    },
    tag_volume_server: {
      title: "Tag Volume",
      description:
        "This table provides insights into the activity of server-side tags. By monitoring changes in volume, you can detect issues early and ensure continuity in your data collection.",
    },
    taganomalyclient: {
      title: "Detected Anomalies",
      description:
        "View detected anomalies in tag firing patterns. The table shows tags with unusual activity, severity levels, and deviation percentages to help you prioritize investigation.",
    },
    taganomalyserver: {
      title: "Detected Anomalies",
      description:
        "View detected anomalies in server-side tag firing patterns. The table shows tags with unusual activity, severity levels, and deviation percentages to help you prioritize investigation.",
    },
    site_performance: {
      title: "Site Performance",
      description:
        "Analyze how tags affect your website’s performance. This overview helps you strike the right balance between accurate data collection and optimal user experience.",
    },
    consent_monitoring: {
      title: "Consent Monitoring",
      description:
        "Stay in control of consent and data collection. This table shows the status of consent settings and helps you remain compliant while optimizing marketing results.",
    },
    cloud_run: {
      title: "Cloud Run Monitoring",
      description:
        "Monitor the health of your Cloud Run instances in real time. Since your entire server-side setup depends on them, this is a crucial component for maintaining a reliable marketing and analytics infrastructure.",
    },
  };

  const [visibleErrorColumns, setVisibleErrorColumns] = useState(null);
  const [visibleTagVolumeColumns, setVisibleTagVolumeColumns] = useState(null);
  const [visibleAnomalyColumns, setVisibleAnomalyColumns] = useState(null);
  const [visibleConsentColumns, setVisibleConsentColumns] = useState(null);
  const errorTableWrapperRef = useRef(null);
  const downloadButtonRef = useRef(null);
  const [downloadBtnPos, setDownloadBtnPos] = useState({ left: null, top: null });

  const handleSearchUrlValue = (e) => setSearchUrlValue(e.target.value);
  const [chartDesc, setChartDesc] = useState(
    tabsInfo["errors_client"]?.description,
  );
  const [tableDesc, setTableDesc] = useState(
    tableInfo["errors_client"]?.description,
  );
  const [tableTitle, setTableTitle] = useState(
    tableInfo["errors_client"]?.title,
  );

  // DatePicker, opened on the period of the homepage when "View more" passes start and end
  const [urlDateRange] = useState(() => getUrlDateRange(searchParams));
  const [startDate, setStartDate] = useState(
    urlDateRange?.start || dayjs().add(-7, "d").set("hour", 0).set("minute", 0).set("second", 0),
  );
  const [endDate, setEndDate] = useState(
    urlDateRange?.end || dayjs().set("hour", 23).set("minute", 59).set("second", 59),
  );

  const timestampFormat = "YYYY-MM-DD";

  const [differenceOneDayOrOneHour, setDifferenceOneDayOrOneHour] =
    useState(urlDateRange?.window ?? null);

  const allRangePresets = [
    { label: "Last 1 Hour", value: [dayjs().subtract(1, "hour"), dayjs()] },
    { label: "Last 24 Hours", value: [dayjs().subtract(24, "hour"), dayjs()] },
    { label: "Today", value: [dayjs().startOf("day"), dayjs()] },
    {
      label: "Last 7 Days",
      value: [dayjs().subtract(6, "day").startOf("day"), dayjs()],
    },
    {
      label: "Last 14 Days",
      value: [dayjs().subtract(13, "day").startOf("day"), dayjs()],
    },
    {
      label: "Last 30 Days",
      value: [dayjs().subtract(29, "day").startOf("day"), dayjs()],
    },
    {
      label: "Last 90 Days",
      value: [dayjs().subtract(89, "day").startOf("day"), dayjs()],
    },
  ];

  const onRangeChange = (dates = [], dateStrings = []) => {
    if (!dates?.[0] || !dates?.[1]) {
      setDefaultDateRange();
      setDifferenceOneDayOrOneHour(null);
      setUpdateLoadFlag(!updateLoadFlag);
      return;
    }

    const dateStart = dates?.length ? dates[0] : "";
    const dateEnd = dates?.length ? dates[1] : "";
    const dateStartStr = dateStart
      ? dateStart.format("YYYY-MM-DD HH:mm:ss")
      : "";
    const dateEndStr = dateEnd ? dateEnd.format("YYYY-MM-DD HH:mm:ss") : "";

    const oneDaySpan = getDateRangeCode(dateStartStr, dateEndStr);
    setDifferenceOneDayOrOneHour(oneDaySpan);

    // If new date has been selected update the charts
    if (dateStart !== startDate || dateEnd !== endDate) {
      if (dateStart && dateEnd && companyID === companyId)
        setUpdateLoadFlag(!updateLoadFlag);
    }

    setStartDate(dateStart);
    setEndDate(dateEnd);
  };

  const setDefaultDateRange = () => {
    setStartDate(
      dayjs().add(-7, "d").set("hour", 0).set("minute", 0).set("second", 0),
    );
    setEndDate(dayjs().set("hour", 23).set("minute", 59).set("second", 59));
  };

  // Keep your existing filtersByView state (around line 180)
  const [filtersByView, setFiltersByView] = useState({
    client: {
      tags: [],
      containerId: [],
      eventName: [],
      domain: [],
      searchUrl: "",
    },
    server: {
      tags: [],
      containerId: [],
      eventName: [],
      domain: [],
      searchUrl: "",
    },
  });

  // Add this useEffect right after filtersByView declaration
  useEffect(() => {
    // Continuously sync current filters to filtersByView
    setFiltersByView((prev) => ({
      ...prev,
      [filtersType]: {
        tags: selectedTags,
        containerId: chosenContainerId,
        eventName: chosenEventName,
        domain: chosenDomain,
        searchUrl: searchUrlValue,
      },
    }));
  }, [
    selectedTags,
    chosenContainerId,
    chosenEventName,
    chosenDomain,
    searchUrlValue,
    filtersType,
  ]);

  const handleDashTypeChangeValue = (
    viewType,
    clickedTabType = "",
    activeTabStr,
  ) => {
    if (viewType === "client" || (viewType === "server" && tagMonitorServer)) {
      const isViewTypeChange = filtersType !== viewType;
      const isTabChange = tabType !== clickedTabType;

      if (isTabChange) {
        setTabType(clickedTabType);
        setActiveTab(activeTabStr);
        handleTabsVisibilityChange(clickedTabType);
        setTagsDropdownList([]);
        setAnomalyToggleOn(false);

        if (isViewTypeChange) {
          // Restore saved filters for the target view
          const savedFilters = filtersByView[viewType];
          setSelectedTags(savedFilters.tags);
          setChosenContainerId(savedFilters.containerId);
          setChosenEventName(savedFilters.eventName);
          setChosenDomain(savedFilters.domain);
          setSearchUrlValue(savedFilters.searchUrl);
          setCurrentSelectedTags(savedFilters.tags);
          setCurrentChosenContainerId(savedFilters.containerId);
          setCurrentChosenEventName(savedFilters.eventName);
          setCurrentChosenDomain(savedFilters.domain);
          setCurrentSearchUrlValue(savedFilters.searchUrl);

          // Trigger data refetch so charts match the restored filters
          setUpdateLoadFlag((prev) => !prev);
        }

        setFiltersType(viewType);
        setMonitoringView(viewType);

        switch (viewType) {
          case "client":
            setFiltersData(filtersClientSide);
            break;
          case "server":
            setFiltersData(filtersServerSide);
            break;
          default:
            setFiltersData([]);
        }

        setChartDesc(tabsInfo[clickedTabType]?.description);
        setTableDesc(tableInfo[clickedTabType]?.description);
        setTableTitle(tableInfo[clickedTabType]?.title);

        updateFilters(viewType);
      }
    }
  };

  const generateDropdownContent = (tagsData) => {
    const tags = [];

    tagsData?.forEach((elem) => {
      if (elem.tag_name && elem.tag_name !== "undefined") {
        tags.push({
          tag_id: elem.tag_id,
          tag_name: elem.tag_name,
          selected: false,
        });
      }
    });

    const dropdownList = [
      ...new Set(
        tags?.sort((a, b) => {
          if (a.tag_name && b.tag_name) {
            const nameA = a.tag_name.toUpperCase();
            const nameB = b.tag_name.toUpperCase();

            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          }
        }),
      ),
    ];

    return dropdownList;
  };

  const updateFilters = () => {
    const cid = [...new Set(filtersData.map((item) => item.container_id))];
    const hosts = [...new Set(filtersData.map((item) => item.hostname))];
    const eventNames = [...new Set(filtersData.map((item) => item.event_name))];

    setCidList(cid);
    setHostsList(hosts);
    setEventNameList(eventNames);

    const tagsDropdownData = generateDropdownContent(tagsList);
    setTagsDropdownList(tagsDropdownData);
  };

  useEffect(() => updateFilters(), [filtersData]);

  

  // Tabs
  const [tabsVisibility, setTabsVisibility] = useState({
    errors_client: true,
    errors_server: false,
    tag_volume_client: false,
    tag_volume_server: false,
    site_performance: false,
    consent_monitoring: false,
    cloud_run: false,
  });

  const handleTabsVisibilityChange = (val) => {
    const updatedTabs = tabsVisibility;
    Object.keys(updatedTabs).forEach(
      (key) => (updatedTabs[key] = key === val ? true : false),
    );
    setTabsVisibility(updatedTabs);
  };

  // Charts
  const [chartsReady, setChartsReady] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [clientErrorsLabels, setClientErrorsLabels] = useState([]);
  const [clientErrorsData, setClientErrorsData] = useState([]);
  const [clientErrorsOptions, setClientErrorsOptions] = useState([]);
  
  const [clientErrorsTable, setClientErrorsTable] = useState([]);
  const [serverErrorsTable, setServerErrorsTable] = useState([]);

  // Tag Diagnostics: latest diagnosis per tag, keyed by tag_id + container_id.
  const [diagnosticsByTag, setDiagnosticsByTag] = useState({});
  // The diagnosis outlives the open flag so the drawer can animate closed with
  // its content still rendered.
  const [openDiagnosis, setOpenDiagnosis] = useState(null);
  const [diagnosisDrawerOpen, setDiagnosisDrawerOpen] = useState(false);
  // Which table row the open drawer belongs to, so it stays marked behind it.
  const [openDiagnosisRowKey, setOpenDiagnosisRowKey] = useState(null);
  // The chip that opened the drawer, so focus can return to it on close.
  const diagnosisTriggerRef = useRef(null);

  const diagnosisKey = (tagId, containerId) => `${tagId || ""}|${containerId || ""}`;

  const diagnosisSeverityRank = { high: 3, medium: 2, low: 1 };

  const [serverErrorsLabels, setServerErrorsLabels] = useState([]);
  const [serverErrorsData, setServerErrorsData] = useState([]);
  const [serverErrorsOptions, setServerErrorsOptions] = useState([]);

  const [tagVolClientData, setTagVolClientData] = useState([]);
  const [tagVolServerData, setTagVolServerData] = useState([]);

  const [tagVolTotalClientLabels, setTagVolTotalClientLabels] = useState([]);
  const [tagVolTotalClientData, setTagVolTotalClientData] = useState([]);
  const [tagVolTotalClientOptions, setTagVolTotalClientOptions] = useState([]);

  const [tagVolTotalServerLabels, setTagVolTotalServerLabels] = useState([]);
  const [tagVolTotalServerData, setTagVolTotalServerData] = useState([]);
  const [tagVolTotalServerOptions, setTagVolTotalServerOptions] = useState([]);

  const [sitePerfPerTagTable, setSitePerfPerTagTable] = useState([]);
  const [sitePerfPerTriggerTable, setSitePerfPerTriggerTable] = useState([]);

  const [sitePerfPerTagLabels, setSitePerfPerTagLabels] = useState([]);
  const [sitePerfPerTagData, setSitePerfPerTagData] = useState([]);
  const [sitePerfPerTagOptions, setSitePerfPerTagOptions] = useState([]);

  const [sitePerfPerTriggerLabels, setSitePerfPerTriggerLabels] = useState([]);
  const [sitePerfPerTriggerData, setSitePerfPerTriggerData] = useState([]);
  const [sitePerfPerTriggerOptions, setSitePerfPerTriggerOptions] = useState(
    [],
  );

  const [consentMonitoringData, setConsentMonitoringData] = useState([]);
  const [cloudRunData, setCloudRunData] = useState([]);

  // snapshot
  const [snapshotToReview, setSnapshotToReview] = useState({});
  const [initialSnapshotsList, setInitialSnapshotsList] = useState([]);
  const [snapshotExpanded, setSnapshotExpanded] = useState(false);
  const [snapshotsList, setSnapshotsList] = useState([]);
  const [aggregatedConsentMonitoringData, setAggregatedConsentMonitoringData] =
    useState([]);

  const [initialData, setInitialData] = useState({});
  const [freshData, setFreshData] = useState({});
  const [initialFilters, setInitialFilters] = useState({});

  const chartGroupOptions = [
    { label: "tag name", value: "tag_name" },
    { label: "event name", value: "event_name" },
  ];

  const [clientErrorsGroupOption, setClientErrorsGroupOption] = useState(
    chartGroupOptions[0].value,
  );
  const [serverErrorsGroupOption, setServerErrorsGroupOption] = useState(
    chartGroupOptions[0].value,
  );
  const [clientTagVolumeGroupOption, setClientTagVolumeGroupOption] = useState(
    chartGroupOptions[0].value,
  );
  const [serverTagVolumeGroupOption, setServerTagVolumeGroupOption] = useState(
    chartGroupOptions[0].value,
  );
  const [sitePerfGroupOption, setSitePerfGroupOption] = useState(
    chartGroupOptions[0].value,
  );

  const [anomalyClientLabels, setAnomalyClientLabels] = useState([]);
  const [anomalyClientData, setAnomalyClientData] = useState([]);
  const [anomalyClientOptions, setAnomalyClientOptions] = useState({});
  const [anomalyClientTable, setAnomalyClientTable] = useState([]);

  const [anomalyServerLabels, setAnomalyServerLabels] = useState([]);
  const [anomalyServerData, setAnomalyServerData] = useState([]);
  const [anomalyServerOptions, setAnomalyServerOptions] = useState({});
  const [anomalyServerTable, setAnomalyServerTable] = useState([]);

  const [anomalyChartsLoading, setAnomalyChartsLoading] = useState(true);

  const [anomalyToggleOn, setAnomalyToggleOn] = useState(false);
  const [anomalyOnlyFilter, setAnomalyOnlyFilter] = useState(false);

  const [clientTagAnomalyGroupOption, setClientTagAnomalyGroupOption] =
    useState(chartGroupOptions[0].value);
  const [serverTagAnomalyGroupOption, setServerTagAnomalyGroupOption] =
    useState(chartGroupOptions[0].value);

  // Anomaly data is hourly, so "Last 1 Hour" is not applicable when anomaly is toggled on
  const rangePresets = anomalyToggleOn
    ? allRangePresets.filter((p) => p.label !== "Last 1 Hour")
    : allRangePresets;

  // Consent type filter runs client-side, because the data already contains every consent type
  const [chosenConsentType, setChosenConsentType] = useState([]);
  const matchesConsentType = (item) =>
    chosenConsentType.length === 0 ||
    chosenConsentType.includes(item.consent_name);
  const filteredConsentMonitoringData = (consentMonitoringData || []).filter(
    matchesConsentType,
  );
  const consentTypeOptions = Array.from(
    new Set((consentMonitoringData || []).map((elem) => elem?.consent_name)),
  )
    .filter(Boolean)
    .map((elem) => ({
      label: elem,
      value: elem,
    }));

  // Function to aggregate the consent monitoring data by consent_name and container_id
  const aggregateConsentMonitoringData = (data) => {
    if (!Array.isArray(data)) {
      return [];
    }

    const aggregatedData = data.reduce((acc, item) => {
      const {
        consent_name,
        tag_name,
        container_id,
        hostname,
        url,
        event_name,
        granted,
        denied,
        total_values,
      } = item;
      const key = `${consent_name}_${tag_name}_${container_id}_${hostname}_${event_name}`;
      if (!acc[key]) {
        acc[key] = {
          consent_name,
          tag_name: tag_name || "-",
          container_id: container_id || "-",
          hostname: hostname || "-",
          url: url || "",
          event_name: event_name || "-",
          granted: 0,
          denied: 0,
          total_values: 0,
        };
      }

      if (url) {
        const currentUrl = acc[key].url || "";
        const currentHasPath = /^https?:\/\/[^/]+\/./i.test(currentUrl);
        const nextHasPath = /^https?:\/\/[^/]+\/./i.test(url);
        if (!currentUrl || (!currentHasPath && nextHasPath)) {
          acc[key].url = url;
        }
      }
      acc[key].granted += granted;
      acc[key].denied += denied;
      acc[key].total_values += total_values;
      return acc;
    }, {});

    return Object.entries(aggregatedData)
      .filter(([_, item]) => item.total_values > 0) // Filter out items with no values (NaN approval)
      .map(([key, item]) => ({
        ...item,
        key: key, // Add unique key for Ant Design Table
        approval_percent: ((item.granted / item.total_values) * 100).toFixed(2),
      }))
      .sort(
        (a, b) =>
          parseFloat(b.approval_percent) - parseFloat(a.approval_percent),
      ); // Sort by approval % descending
  };

  const handleSnapshotAreaUpdate = (row) => {
    if (snapshotToReview === row) {
      setSnapshotExpanded(!snapshotExpanded);
    } else {
      setSnapshotExpanded(true);
    }
    setSnapshotToReview(row);
  };

  const updateCharts = (context, chartType) => {
    setChartsReady(false);

    const {
      error_query_client,
      error_query_server,
      tag_vol_query_client,
      tag_vol_query_server,
      tag_vol_total_calls_client,
      tag_vol_total_calls_server,
      anomaly_data_client,
      anomaly_data_server,
      site_performance_per_tag,
      site_performance_per_trigger,
      consent_monitoring,
      cloud_run,
    } = context;

    // Filter excluded tags from data arrays
    const f_error_query_client = filterExcludedFromData(error_query_client);
    const f_error_query_server = filterExcludedFromData(error_query_server);
    const f_tag_vol_query_client = filterExcludedFromData(tag_vol_query_client);
    const f_tag_vol_query_server = filterExcludedFromData(tag_vol_query_server);
    const f_tag_vol_total_calls_client = filterExcludedFromData(tag_vol_total_calls_client);
    const f_tag_vol_total_calls_server = filterExcludedFromData(tag_vol_total_calls_server);
    const f_site_performance_per_tag = filterExcludedFromData(site_performance_per_tag);
    const f_site_performance_per_trigger = filterExcludedFromData(site_performance_per_trigger);

    switch (chartType) {
      case "errors":
        setClientErrorsData(
          renderLineChart({
            dataArr: f_error_query_client,
            aggrParam: "total_fails_percent",
            setLabelFunc: setClientErrorsLabels,
            chartOptionFunc: setClientErrorsOptions,
            chartOptionType: "error-monitoring",
            chartElementsNum: chartEntriesNum,
            groupByOption: clientErrorsGroupOption,
          }),
        );

        setServerErrorsData(
          renderLineChart({
            dataArr: f_error_query_server,
            aggrParam: "total_fails_percent",
            setLabelFunc: setServerErrorsLabels,
            chartOptionFunc: setServerErrorsOptions,
            chartOptionType: "error-monitoring",
            chartElementsNum: chartEntriesNum,
            groupByOption: serverErrorsGroupOption,
          }),
        );

        // Rendering tables
        setClientErrorsTable(aggregateErrorsByTagName(f_error_query_client));
        setServerErrorsTable(aggregateErrorsByTagName(f_error_query_server));
        break;

      case "tag-vol":
        setTagVolTotalClientData(
          renderLineChart({
            dataArr: f_tag_vol_total_calls_client,
            aggrParam: "total_calls",
            setLabelFunc: setTagVolTotalClientLabels,
            chartOptionFunc: setTagVolTotalClientOptions,
            chartOptionType: "tag-volume",
            chartElementsNum: chartEntriesNum,
            groupByOption: clientTagVolumeGroupOption,
          }),
        );

        setTagVolTotalServerData(
          renderLineChart({
            dataArr: f_tag_vol_total_calls_server,
            aggrParam: "total_calls",
            setLabelFunc: setTagVolTotalServerLabels,
            chartOptionFunc: setTagVolTotalServerOptions,
            chartOptionType: "tag-volume",
            chartElementsNum: chartEntriesNum,
            groupByOption: serverTagVolumeGroupOption,
          }),
        );

        setTagVolClientData(transformTableData(f_tag_vol_query_client));
        setTagVolServerData(transformTableData(f_tag_vol_query_server));
        break;

      case "tag-anomaly":
        setAnomalyClientData(
          renderLineChart({
            dataArr: anomaly_data_client,
            aggrParam: "tagfiredcountlog",
            setLabelFunc: setAnomalyClientLabels,
            chartOptionFunc: setAnomalyClientOptions,
            chartOptionType: "anomaly-monitoring",
            chartElementsNum: chartEntriesNum,
            groupByOption: clientTagAnomalyGroupOption,
            aggregateAllTags: true,
          }),
        );

        setAnomalyServerData(
          renderLineChart({
            dataArr: anomaly_data_server,
            aggrParam: "tagfiredcountlog",
            setLabelFunc: setAnomalyServerLabels,
            chartOptionFunc: setAnomalyServerOptions,
            chartOptionType: "anomaly-monitoring",
            chartElementsNum: chartEntriesNum,
            groupByOption: serverTagAnomalyGroupOption,
            aggregateAllTags: true,
          }),
        );

        setAnomalyClientTable(aggregateAnomalyByTagName(anomaly_data_client));
        setAnomalyServerTable(aggregateAnomalyByTagName(anomaly_data_server));
        break;

      case "site-performance":
        setSitePerfPerTagData(
          renderLineChart({
            dataArr: f_site_performance_per_tag,
            aggrParam: "avg_ex_time",
            setLabelFunc: setSitePerfPerTagLabels,
            chartOptionFunc: setSitePerfPerTagOptions,
            chartOptionType: "site-performance",
            chartElementsNum: chartEntriesNum,
            groupByOption: sitePerfGroupOption,
          }),
        );

        setSitePerfPerTriggerData(
          renderLineChart({
            dataArr: f_site_performance_per_trigger,
            aggrParam: "avg_ex_time",
            setLabelFunc: setSitePerfPerTriggerLabels,
            chartOptionFunc: setSitePerfPerTriggerOptions,
            chartOptionType: "site-performance",
            chartElementsNum: chartEntriesNum,
            groupByOption: sitePerfGroupOption,
          }),
        );

        setSitePerfPerTagTable(
          aggregateErrorsSitePerformanceByTagNameData(f_site_performance_per_tag),
        );
        setSitePerfPerTriggerTable(
          aggregateErrorsSitePerformanceByEventNameData(
            f_site_performance_per_trigger,
          ),
        );
        break;
      case "consent-monitoring":
        setConsentMonitoringData(consent_monitoring);
        setAggregatedConsentMonitoringData(
          transformTableData(
            aggregateConsentMonitoringData(consent_monitoring),
          ),
        );
        break;
      case "cloud-run":
        setCloudRunData(transformTableData(cloud_run));
        break;

      case "all":
        //errors
        setClientErrorsData(
          renderLineChart({
            dataArr: f_error_query_client,
            aggrParam: "total_fails_percent",
            setLabelFunc: setClientErrorsLabels,
            chartOptionFunc: setClientErrorsOptions,
            chartOptionType: "error-monitoring",
            chartElementsNum: chartEntriesNum,
            groupByOption: clientErrorsGroupOption,
          }),
        );

        setServerErrorsData(
          renderLineChart({
            dataArr: f_error_query_server,
            aggrParam: "total_fails_percent",
            setLabelFunc: setServerErrorsLabels,
            chartOptionFunc: setServerErrorsOptions,
            chartOptionType: "error-monitoring",
            chartElementsNum: chartEntriesNum,
            groupByOption: serverErrorsGroupOption,
          }),
        );

        setClientErrorsTable(aggregateErrorsByTagName(f_error_query_client));
        setServerErrorsTable(aggregateErrorsByTagName(f_error_query_server));

        //tag vol
        setTagVolTotalClientData(
          renderLineChart({
            dataArr: f_tag_vol_total_calls_client,
            aggrParam: "total_calls",
            setLabelFunc: setTagVolTotalClientLabels,
            chartOptionFunc: setTagVolTotalClientOptions,
            chartOptionType: "tag-volume",
            chartElementsNum: chartEntriesNum,
            groupByOption: clientTagVolumeGroupOption,
          }),
        );

        setTagVolTotalServerData(
          renderLineChart({
            dataArr: f_tag_vol_total_calls_server,
            aggrParam: "total_calls",
            setLabelFunc: setTagVolTotalServerLabels,
            chartOptionFunc: setTagVolTotalServerOptions,
            chartOptionType: "tag-volume",
            chartElementsNum: chartEntriesNum,
            groupByOption: serverTagVolumeGroupOption,
          }),
        );

        setTagVolClientData(transformTableData(f_tag_vol_query_client));
        setTagVolServerData(transformTableData(f_tag_vol_query_server));

        //tag anomaly
        setAnomalyClientData(
          renderLineChart({
            dataArr: anomaly_data_client,
            aggrParam: "tagfiredcountlog",
            setLabelFunc: setAnomalyClientLabels,
            chartOptionFunc: setAnomalyClientOptions,
            chartOptionType: "anomaly-monitoring",
            chartElementsNum: chartEntriesNum,
            groupByOption: clientTagAnomalyGroupOption,
            aggregateAllTags: true,
          }),
        );

        setAnomalyServerData(
          renderLineChart({
            dataArr: anomaly_data_server,
            aggrParam: "tagfiredcountlog",
            setLabelFunc: setAnomalyServerLabels,
            chartOptionFunc: setAnomalyServerOptions,
            chartOptionType: "anomaly-monitoring",
            chartElementsNum: chartEntriesNum,
            groupByOption: serverTagAnomalyGroupOption,
            aggregateAllTags: true,
          }),
        );

        setAnomalyClientTable(aggregateAnomalyByTagName(anomaly_data_client));
        setAnomalyServerTable(aggregateAnomalyByTagName(anomaly_data_server));

        //site performance
        setSitePerfPerTagData(
          renderLineChart({
            dataArr: f_site_performance_per_tag,
            aggrParam: "avg_ex_time",
            setLabelFunc: setSitePerfPerTagLabels,
            chartOptionFunc: setSitePerfPerTagOptions,
            chartOptionType: "site-performance",
            chartElementsNum: chartEntriesNum,
            groupByOption: sitePerfGroupOption,
          }),
        );

        setSitePerfPerTriggerData(
          renderLineChart({
            dataArr: f_site_performance_per_trigger,
            aggrParam: "avg_ex_time",
            setLabelFunc: setSitePerfPerTriggerLabels,
            chartOptionFunc: setSitePerfPerTriggerOptions,
            chartOptionType: "site-performance",
            chartElementsNum: chartEntriesNum,
            groupByOption: sitePerfGroupOption,
          }),
        );

        setSitePerfPerTagTable(
          aggregateErrorsSitePerformanceByTagNameData(f_site_performance_per_tag),
        );
        setSitePerfPerTriggerTable(
          aggregateErrorsSitePerformanceByEventNameData(
            f_site_performance_per_trigger,
          ),
        );

        setConsentMonitoringData(consent_monitoring);
        setAggregatedConsentMonitoringData(
          transformTableData(
            aggregateConsentMonitoringData(consent_monitoring),
          ),
        );

        setCloudRunData(transformTableData(cloud_run));

        break;
    }

    // After all chart updates are done, set charts as ready
    if (isFirstLoad) {
      setTimeout(() => {
        setChartsReady(true);
        setIsFirstLoad(false);
      }, 7000);
    } else {
      setChartsReady(true);
    }
  };

  // Parse a chart timestamp ("YYYY-MM-DD H:MM", "HH:MM", or "YYYY-MM-DD") to a
  // sortable number — hours aren't zero-padded, so a plain string sort misorders them.
  const parseChartTimestamp = (ts) => {
    if (!ts) return 0;
    const [datePart, timePart] = ts.split(" ");
    if (timePart) {
      const [year, month, day] = datePart.split("-").map(Number);
      const [hours, minutes] = timePart.split(":").map(Number);
      return new Date(year, month - 1, day, hours, minutes).getTime();
    }
    if (ts.includes(":")) {
      const [hours, minutes] = ts.split(":").map(Number);
      return hours * 60 + minutes;
    }
    const [year, month, day] = ts.split("-").map(Number);
    return new Date(year, month - 1, day).getTime();
  };

  const retrieveTimestampLabels = (arr, labelField) => {
    return [...new Set(arr.map((item) => item[labelField]))].sort(
      (a, b) => parseChartTimestamp(a) - parseChartTimestamp(b),
    );
  };

  const generateTimestamps = (startDate, endDate) => {
    const timestampsList = [];
    const now = new Date();

    const amsterdamTime = now.toLocaleString("en-US", {
      timeZone: clientTimeStamp,
    });
    const amsterdamHour = new Date(amsterdamTime).getHours();
    const amsterdamDate = new Date(amsterdamTime);

    const lastHourStart = new Date(
      amsterdamDate.setHours(amsterdamDate.getHours() - 1),
    );
    lastHourStart.setMinutes(amsterdamDate.getMinutes());

    const timeSpan = companyID !== companyId ? null : differenceOneDayOrOneHour;

    // When company changes, use reset dates instead of current state
    let startDateNewDate = companyID !== companyId ? dayjs().add(-7, "d") : startDate;
    let endDateNewDate = companyID !== companyId ? dayjs() : endDate;

    const formatDate = (date) => {
      // Extract year, month, and day
      let year = date.getFullYear();
      let month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-indexed, so we add 1
      let day = date.getDate().toString().padStart(2, "0");

      // Format the date as YYYY-MM-DD
      let formattedDate = `${year}-${month}-${day}`;
      return formattedDate;
    };

    switch (timeSpan) {
      case "one_day":
      case "last_24_hours":
        for (let i = amsterdamHour; i >= 0; i--) {
          const hour = `${i.toString()}:00`;
          const date = formatDate(new Date(endDateNewDate));
          const fullDateString = `${date} ${hour}`;
          timestampsList.push(fullDateString);
        }

        for (let i = 23; i >= amsterdamHour; i--) {
          const hour = `${i.toString()}:00`;
          const date = formatDate(new Date(startDateNewDate));
          const fullDateString = `${date} ${hour}`;
          timestampsList.push(fullDateString);
        }
        break;

      case "last_hour":
        for (let i = 0; i < 60; i++) {
          const timeEntry = new Date(lastHourStart.getTime() + i * 60000);
          const hours = ("0" + timeEntry.getHours()).slice(-2); // Extract hours and pad with leading zero if necessary
          const minutes = ("0" + timeEntry.getMinutes()).slice(-2); // Extract minutes and pad with leading zero if necessary
          const formattedTime = `${hours}:${minutes}`;
          timestampsList.push(formattedTime);
        }
        break;

      case "today":
        for (let i = 0; i <= amsterdamHour; i++) {
          const hour = `${i.toString()}:00`;
          const date = formatDate(new Date(now));
          const fullDateString = `${date} ${hour}`;
          timestampsList.push(fullDateString);
        }
        break;

      default:
        while (startDateNewDate <= endDateNewDate) {
          timestampsList.push(startDateNewDate.format("YYYY-MM-DD")); // Format as "YYYY-MM-DD"
          startDateNewDate = startDateNewDate.add(1, "d"); // Increment the date by 1 day
        }
        break;
    }

    return timestampsList;
  };

  const addMissingTimestamps = (list, aggrParam) => {
    const timestampsList = generateTimestamps(startDate, endDate);

    // Group the results by the combination of tag_id and container_id
    const groupedData = {};
    const errordata = aggrParam === "total_fails_percent" ? true : false;
    const sitePerfData = aggrParam === "avg_ex_time" ? true : false;

    list?.forEach((item) => {
      const key = `${item.tag_id}_${item.container_id}`;

      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      groupedData[key].push(item);
    });

    // Add missing timestamps with 0 total_calls for other tags
    for (const key in groupedData) {
      let currentTagData = groupedData[key];

      if (currentTagData) {
        const missingTimestamps = timestampsList.filter(
          (timestamp) =>
            !currentTagData.some((item) => item.timestamp === timestamp),
        );

        if (currentTagData.length > 0) {
          missingTimestamps.forEach((timestamp) => {
            currentTagData.push({
              container_id: currentTagData[0].container_id,
              tag_id: currentTagData[0].tag_id,
              tag_name: currentTagData[0].tag_name,
              timestamp: timestamp,
              avg_ex_time: sitePerfData ? 0 : null,
              total_calls: errordata ? 0 : null,
              data_type: errordata ? currentTagData[0].data_type : null,
              total_fails: errordata ? 0 : null,
              total_fails_percent: errordata ? 0 : null,
            });
          });
        }
      }
    }

    // extract all objects inside the nested arrays and create a new list
    let arrayOfArrays = Object.values(groupedData);
    const results = arrayOfArrays.reduce((accumulator, currentArray) => {
      return [...accumulator, ...currentArray];
    }, []);

    return results;
  };

    const getComparisonPeriodText = () => {
        if (!differenceOneDayOrOneHour) {
            // Calculate days between start and end date
            const days = endDate.diff(startDate, 'day') + 1;
            return `Difference compared to last period of ${days} day${days > 1 ? 's' : ''}`;
        }
        
        switch (differenceOneDayOrOneHour) {
            case 'last_hour':
                return 'Change compared to last hour';
            case 'last_24_hours':
                return 'Change compared to last 24 hours';
            case 'today':
                return 'Change compared to yesterday';
            case 'one_day':
                return 'Change compared to previous day';
            default:
                return 'Change compared to previous period';
        }
    };

    const formatNumber = (num) => {
        if (!num && num !== 0) return '-';
        
        const value = typeof num === 'string' ? parseFloat(num) : num;
        
        if (value >= 1000000) {
            return `${(value / 1000000).toFixed(1)}M`;
        } else if (value >= 1000) {
            return value.toLocaleString('nl-NL');
        }
        return value.toString();
    };

  const getTimestamps = (dataArr, aggrParam, setLabelFunc) => {
    let sortedData;

    const addedMissingTimestampsArrData = addMissingTimestamps(
      dataArr,
      aggrParam,
    );
    const timeSpan = companyID !== companyId ? null : differenceOneDayOrOneHour;

    switch (timeSpan) {
      case "one_day":
      case "last_24_hours":
      case "today":
        // sort the timestamps when they are in the format of "YYYY-MM-DD HH:MM"
        function parseTimestamp(timestamp) {
          if (!timestamp) return new Date(0); // Return a default date if timestamp is undefined
          const [date, time] = timestamp.split(" ");
          if (!date || !time) return new Date(0); // Return a default date if date or time is undefined
          const [year, month, day] = date.split("-").map(Number);
          const [hours, minutes] = time.split(":").map(Number);
          return new Date(year, month - 1, day, hours, minutes);
        }

        // Sort the array of objects by timestamp property in ascending order
        sortedData = addedMissingTimestampsArrData.sort(
          (a, b) => parseTimestamp(a.timestamp) - parseTimestamp(b.timestamp),
        );
        break;

      case "last_hour":
        // sort the timestamps when they are in the format of "HH:MM"
        function timeToMinutes(time) {
          if (!time) return 0; // Return 0 if time is undefined
          const [hours, minutes] = time.split(":").map(Number);
          return hours * 60 + minutes;
        }

        // Sort the array of objects by timestamp property in ascending order
        sortedData = addedMissingTimestampsArrData.sort(
          (a, b) => timeToMinutes(a.timestamp) - timeToMinutes(b.timestamp),
        );
        break;

      default:
        // sort the timestamps when they are in the format of regular dates
        sortedData = addedMissingTimestampsArrData.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
        );
    }

    setLabelFunc(retrieveTimestampLabels(sortedData, "timestamp"));
    return sortedData;
  };

  const renderLineChart = (props) => {
    const {
      dataArr,
      aggrParam,
      setLabelFunc,
      chartOptionType,
      chartOptionFunc,
      chartElementsNum = 10,
      groupByOption = "tag_name",
      aggregateAllTags = true,
    } = props;

    if (chartOptionType === "anomaly-monitoring") {
      // Build anomaly chart datasets from data (aggregated or filtered)
      const buildAnomalyDatasets = (data, label, isAggregate) => {
        const datasets = [];
        const baseColor = "#09003D";
        const anomalyColor = "#F62064";
        const expectedRangeColor = "rgba(207, 255, 94, 0.2)";
        const expectedRangeBorder = "rgb(207, 255, 94)";
        const marginBandColor = "rgba(207, 255, 94, 0.10)";
        // Round plotted values so points sit on the gridline they read as;
        // detection still uses the raw actual_* fields (see isRowAnomalous).
        const actualData = data.map((row) => Math.round(row.tagfiredcountlog || 0));
        const lowerData = data.map((row) => Math.round(row.yhatlower || 0));
        const upperData = data.map((row) => Math.round(row.yhatupper || 0));
        const anomalyFlags = data.map((row) => Number(row.anomaly) !== 0 ? 1 : 0);
        // How many individual tags were anomalous at each timestamp (overview only).
        const anomalyCounts = data.map((row) => row.anomalyCount || 0);
        // Names of the individual tags that were anomalous at each timestamp, so
        // the tooltip can name them instead of just counting them.
        const anomalyTagNames = data.map((row) => row.anomalyTagNames || []);
        // Margin band edges: percentage of the bound, floored to a min width of 10
        // (same guard used to decide flagging, see isRowAnomalous).
        const marginLowerData = lowerData.map(marginLowerEdge).map(Math.round);
        const marginUpperData = upperData.map(marginUpperEdge).map(Math.round);

        // Points are radius 0 (no dots drawn), so give every line a hit area —
        // that's what lets the tooltip fire when hovering the band edges.
        const pointHitRadius = 8;

        // Margin zone (light green band)
        datasets.push({
          label: "Margin Lower",
          data: marginLowerData,
          borderColor: "transparent",
          backgroundColor: "transparent",
          borderWidth: 0,
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          pointHitRadius,
        });

        datasets.push({
          label: "Margin Zone",
          data: marginUpperData,
          borderColor: "transparent",
          backgroundColor: marginBandColor,
          borderWidth: 0,
          fill: '-1',
          tension: 0.4,
          pointRadius: 0,
          pointHitRadius,
        });

        // Expected range (green band)
        datasets.push({
          label: "Lower Bound",
          data: lowerData,
          borderColor: "transparent",
          backgroundColor: "transparent",
          borderWidth: 0,
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          pointHitRadius,
        });

        datasets.push({
          label: "Expected Range",
          data: upperData,
          borderColor: expectedRangeBorder,
          backgroundColor: expectedRangeColor,
          borderWidth: 1,
          borderDash: [5, 5],
          fill: '-1',
          tension: 0.4,
          pointRadius: 0,
          pointHitRadius,
        });

        const actualDataset = {
          label: label,
          data: actualData,
          borderColor: baseColor,
          backgroundColor: baseColor,
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointHoverRadius: 5,
          pointHitRadius,
          pointRadius: anomalyFlags.map((f) => (f === 1 ? 4 : 0)),
          pointBackgroundColor: anomalyFlags.map((f) => (f === 1 ? anomalyColor : baseColor)),
          // Surfaced in the tooltip so a marker reads as "N tags anomalous this hour".
          anomalyCounts: anomalyCounts,
          anomalyTagNames: anomalyTagNames,
        };

        // Anomalies are surfaced as point markers + tooltip; the line itself
        // stays a single neutral colour.
        datasets.push(actualDataset);

        return datasets;
      };

      let sortedData;

      if (aggregateAllTags) {
        // Aggregate all tags by timestamp for overview
        const timestampMap = new Map();
        dataArr?.forEach((row) => {
          const ts = row.timestamp;
          // Skip rows without a prediction (yhat bounds 0), matching Looker's totals.
          if (Number(row.yhatlower) <= 0 && Number(row.yhatupper) <= 0) return;
          if (!timestampMap.has(ts)) {
            timestampMap.set(ts, {
              timestamp: ts,
              tagfiredcountlog: 0,
              yhatlower: 0,
              yhatupper: 0,
              anomalyCount: 0,
              anomalyTagNames: [],
            });
          }
          const entry = timestampMap.get(ts);
          // actual_* are already real counts (SQL exp'd them per row), so sum directly
          entry.tagfiredcountlog += row.actual_count || 0;
          entry.yhatlower += row.actual_yhat_lower || 0;
          entry.yhatupper += row.actual_yhat_upper || 0;
          if (isRowAnomalous(row)) {
            entry.anomalyCount++;
            // Track which tag fired the anomaly (with its severity) so the tooltip
            // can name it and order the list by severity.
            const name = row.tagname || row.tagid;
            if (name && !entry.anomalyTagNames.some((t) => t.name === name)) {
              entry.anomalyTagNames.push({ name, severity: row.severity || 'info' });
            }
          }
        });

        // Order each hour's anomalous tags critical → warning → info so the most
        // urgent tags sit at the top of the tooltip list.
        const tooltipSeverityRank = { critical: 3, warning: 2, info: 1, normal: 0 };
        sortedData = Array.from(timestampMap.values())
          .map((entry) => ({
            ...entry,
            anomaly: entry.anomalyCount > 0 ? 1 : 0,
            anomalyTagNames: [...entry.anomalyTagNames].sort(
              (a, b) =>
                (tooltipSeverityRank[b.severity] || 0) -
                (tooltipSeverityRank[a.severity] || 0),
            ),
          }))
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      } else {
        // Show filtered data for selected tag — aggregate by timestamp
        // so we get one point per hour even if there are multiple rows
        const timestampMap = new Map();
        dataArr?.forEach((row) => {
          const ts = row.timestamp;
          // Skip rows without a prediction (yhat bounds 0), matching Looker's totals.
          if (Number(row.yhatlower) <= 0 && Number(row.yhatupper) <= 0) return;
          if (!timestampMap.has(ts)) {
            timestampMap.set(ts, {
              timestamp: ts,
              tagfiredcountlog: 0,
              yhatlower: 0,
              yhatupper: 0,
              anomalyCount: 0,
              tagname: row.tagname,
            });
          }
          const entry = timestampMap.get(ts);
          // actual_* are already real counts (SQL exp'd them per row), so sum directly
          entry.tagfiredcountlog += row.actual_count || 0;
          entry.yhatlower += row.actual_yhat_lower || 0;
          entry.yhatupper += row.actual_yhat_upper || 0;
          if (isRowAnomalous(row)) entry.anomalyCount++;
        });

        sortedData = Array.from(timestampMap.values())
          .map((entry) => ({
            ...entry,
            anomaly: entry.anomalyCount > 0 ? 1 : 0,
          }))
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      }

      const sortedTimestamps = sortedData.map((row) => row.timestamp);
      setLabelFunc(sortedTimestamps);

      // Name the series after the tag when only one is charted; otherwise it's
      // the all-tag total.
      const distinctTagNames = [
        ...new Set((dataArr || []).map((r) => r.tagname || r.tagid).filter(Boolean)),
      ];
      const chartLabel =
        distinctTagNames.length === 1
          ? distinctTagNames[0]
          : aggregateAllTags
            ? "Actual Count"
            : sortedData[0]?.tagname || "Selected Tag";
      const datasets = buildAnomalyDatasets(sortedData, chartLabel, aggregateAllTags)
        .map((dataset) => ({
          ...dataset,
          noData: dataArr?.length === 0,
        }));

      let maxValue = 0;
      if (datasets.length > 0) {
        const valueList = datasets.flatMap((dataset) => dataset.data);
        if (valueList.length > 0) {
          maxValue = Math.max(...valueList);
        }
      }

      chartOptionFunc(errorsChartOptions("anomaly-monitoring", maxValue));
      return datasets;
    }

    let sortedData = getTimestamps(dataArr, aggrParam, setLabelFunc);
    // Pass the x-axis label order to convertArrToChartData so each series' data
    // aligns to it by index instead of being sorted independently.
    const orderedLabels = retrieveTimestampLabels(sortedData, "timestamp");
    const dataGroupedByTagAndContainerId = {};

    sortedData.forEach((elem) => {
      const key =
        groupByOption === "event_name"
          ? `${elem.event_name}_${elem.container_id}`
          : `${elem.tag_id}_${elem.container_id}`;
      if (!dataGroupedByTagAndContainerId[key]) {
        dataGroupedByTagAndContainerId[key] = {
          tagId: elem.tag_id,
          containerId: elem.container_id,
          tagName: elem.tag_name,
          eventName: elem.event_name,
          rows: [],
          aggrValue: 0,
        };
      }

      dataGroupedByTagAndContainerId[key].rows.push(elem);
      dataGroupedByTagAndContainerId[key].aggrValue += elem[aggrParam];
    });

    const sortedGroupedData = Object.keys(dataGroupedByTagAndContainerId)
      .sort(
        (a, b) =>
          dataGroupedByTagAndContainerId[b].aggrValue -
          dataGroupedByTagAndContainerId[a].aggrValue,
      )
      .map((key) => dataGroupedByTagAndContainerId[key]);

    return convertArrToChartData({
      dataArr: sortedGroupedData,
      aggrParam,
      chartOptionType,
      chartOptionFunc,
      chartElementsNum,
      groupByOption,
      orderedLabels,
    });
  };

  const aggregateErrors = (dataArr) => {
    let aggregateErrorsByTagName = [];

    aggregateErrorsByTagName = dataArr.map((elem) => {
      const columns = {
        aggrValue: elem.aggrValue,
        containerId: elem.containerId,
        tagId: elem.tagId,
        tagName: elem.tagName,
        eventName: elem.eventName,
      };

      const rows = [];

      for (let row of elem.rows) {
        const newRow = {
          container_id: row.container_id,
          data_type: row.data_type,
          tag_id: row.tag_id,
          tag_name: row.tag_name,
          event_name: row.event_name,
          timestamp: row.timestamp,
          total_calls: row.total_calls,
          total_fails: row.total_fails,
          total_fails_percent: row.total_fails_percent,
        };

        const exists = rows.some(
          (obj) =>
            elem.containerId === obj.container_id &&
            elem.tagId === obj.tag_id &&
            row.timestamp === obj.timestamp &&
            row.tag_name === obj.tag_name &&
            row.event_name === obj.event_name,
        );

        if (!exists) {
          rows.push(newRow);
        } else {
          const sameRow = rows.find(
            (obj) =>
              elem.containerId === obj.container_id &&
              elem.tagId === obj.tag_id &&
              row.timestamp === obj.timestamp &&
              row.tag_name === obj.tag_name &&
              row.event_name === obj.event_name,
          );

          sameRow["total_calls"] += row["total_calls"];
          sameRow["total_fails"] += row["total_fails"];
          sameRow["total_fails_percent"] =
            sameRow["total_calls"] == 0
              ? 0
              : (
                  (sameRow["total_fails"] / sameRow["total_calls"]) *
                  100
                ).toFixed(2);

          Object.assign(sameRow, sameRow);
        }
      }

      columns["rows"] = rows;
      return columns;
    });

    return aggregateErrorsByTagName;
  };

  const aggregateSitePerf = (dataArr) => {
    let aggregateSitePerfByTagName = [];

    aggregateSitePerfByTagName = dataArr.map((elem) => {
      const columns = {
        aggrValue: elem.aggrValue,
        containerId: elem.containerId,
        tagId: elem.tagId,
        tagName: elem.tagName,
        eventName: elem.eventName, // Include event_name in the aggregation
      };

      const rows = [];

      if (elem.rows) {
        for (let row of elem.rows) {
          const newRow = {
            container_id: row.container_id,
            data_type: row.data_type,
            tag_id: row.tag_id,
            tag_name: row.tag_name,
            event_name: row.event_name, // Include event_name in the aggregation
            timestamp: row.timestamp,
            avg_ex_time: row.avg_ex_time,
          };

          const exists = rows.some(
            (obj) =>
              elem.containerId === obj.container_id &&
              elem.tagId === obj.tag_id &&
              row.timestamp === obj.timestamp &&
              row.tag_name === obj.tag_name &&
              row.event_name === obj.event_name, // Include event_name in the aggregation
          );

          if (!exists) {
            rows.push(newRow);
          } else {
            const sameRow = rows.find(
              (obj) =>
                elem.containerId === obj.container_id &&
                elem.tagId === obj.tag_id &&
                row.timestamp === obj.timestamp &&
                row.tag_name === obj.tag_name &&
                row.event_name === obj.event_name,
            );

            sameRow["avg_ex_time"] = (
              parseFloat(sameRow["avg_ex_time"]) +
              parseFloat(row["avg_ex_time"])
            ).toFixed(2);

            Object.assign(sameRow, sameRow);
          }
        }
      }

      columns["rows"] = rows;
      return columns;
    });

    return aggregateSitePerfByTagName;
  };

  const convertArrToChartData = (props) => {
    const {
      dataArr,
      aggrParam,
      chartOptionType,
      chartOptionFunc,
      chartElementsNum = 10,
      groupByOption = "tag_name",
      orderedLabels = [],
    } = props;

    let aggregatedData = [];

    const tagColors = [
      "#09003D",
      "#0082F1",
      "#CFFF5E",
      "#00A359",
      "#F62064",
      "#C78FFF",
      "#D8CAD8",
      "#FFB84C",
      "#942828",
      "#EECEFF",
      "#5392C1",
      "#B5404E",
      "#F9DCFE",
      "#ECA0A9",
      "#D6F8D6",
      "#77AC8A",
      "#48DFFF",
      "#004986",
      "#36013F",
      "#7A144C",
      "#4A0093",
      "#2B2BB5",
      "#80E764",
      "#FFDFAE",
      "#136761",
      "#FF5E5B",
      "#FF4400",
      "#874AD9",
      "#FF8718",
      "#563676",
    ];

    if (chartOptionType === "error-monitoring") {
      aggregatedData = aggregateErrors(dataArr);
    } else if (chartOptionType === "site-performance") {
      aggregatedData = aggregateSitePerf(dataArr);
    }

    const shortenedDataArr =
      chartOptionType === "error-monitoring" ||
      chartOptionType === "site-performance"
        ? aggregatedData.slice(0, chartElementsNum)
        : dataArr.slice(0, chartElementsNum);

    // Create a Map to store unique labels and their combined data
    const uniqueLabelsMap = new Map();

    shortenedDataArr.forEach((elem) => {
      const combinations = [];

      if (groupByOption === "tag_name") {
        const tagName =
          elem.tagName !== "undefined" && elem.tagName
            ? elem.tagName
            : elem.tagId;
        if (tagName !== "undefined") {
          combinations.push({
            label: tagName,
            rows: elem.rows.filter((row) => row.timestamp),
          });
        }
      } else {
        elem.rows.forEach((row) => {
          if (row.event_name && row.event_name !== "undefined") {
            const eventKey = `${row.event_name}`;
            if (!combinations.some((c) => c.label === eventKey)) {
              combinations.push({
                label: eventKey,
                rows: elem.rows.filter(
                  (r) => r.event_name === row.event_name && r.timestamp,
                ),
              });
            }
          }
        });
      }

      combinations.forEach((combo) => {
        if (!uniqueLabelsMap.has(combo.label)) {
          uniqueLabelsMap.set(combo.label, {
            label: combo.label,
            rows: [...combo.rows],
          });
        } else {
          const existing = uniqueLabelsMap.get(combo.label);
          const existingTimestamps = new Set(
            existing.rows.map((row) => row.timestamp),
          );

          combo.rows.forEach((row) => {
            if (!existingTimestamps.has(row.timestamp)) {
              existing.rows.push(row);
            }
          });
        }
      });
    });

    // Convert Map to array and create chart data with zero-filling
    const chartData = Array.from(uniqueLabelsMap.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .slice(0, chartElementsNum)
      .map((elem, idx) => {
        // Build timestamp -> value map.
        // For total_fails_percent we must aggregate the underlying counts and
        // recompute the ratio — summing derived percentages across event_names
        // (e.g. a tag that fires on many events) produces values > 100%.
        let timestampMap;
        if (aggrParam === "total_fails_percent") {
          const callsPerTs = new Map();
          const failsPerTs = new Map();
          elem.rows.forEach((row) => {
            if (row.timestamp != null) {
              callsPerTs.set(row.timestamp, (callsPerTs.get(row.timestamp) ?? 0) + (row.total_calls ?? 0));
              failsPerTs.set(row.timestamp, (failsPerTs.get(row.timestamp) ?? 0) + (row.total_fails ?? 0));
            }
          });
          timestampMap = new Map();
          callsPerTs.forEach((totalCalls, ts) => {
            const totalFails = failsPerTs.get(ts) ?? 0;
            timestampMap.set(ts, totalCalls > 0 ? parseFloat(((totalFails / totalCalls) * 100).toFixed(2)) : 0);
          });
        } else {
          // For count-based metrics (total_calls, avg_ex_time) sum across event_names
          timestampMap = new Map();
          elem.rows.forEach((row) => {
            if (row.timestamp != null) {
              const prev = timestampMap.get(row.timestamp) ?? 0;
              timestampMap.set(row.timestamp, prev + (row[aggrParam] ?? 0));
            }
          });
        }

        // Align each value to the x-axis label order so data[i] always
        // corresponds to labels[i]; missing timestamps render as 0.
        const data = orderedLabels.map(
          (timestamp) => timestampMap.get(timestamp) ?? 0,
        );

        return {
          label: elem.label,
          data: data,
          borderColor: tagColors[idx],
          backgroundColor: tagColors[idx],
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointRadius: 0,
        };
      });

    // Calculate max value for Y axis
    let maxValue;
    const valueList = [];
    if (chartData.length > 0) {
      for (let data of chartData) {
        valueList.push(...data.data);
      }
    }
    if (valueList.length > 0) {
      maxValue = Math.max(...valueList);
    }

    chartOptionFunc(errorsChartOptions(chartOptionType, maxValue));

    if (chartData.length === 0) {
      return [{
        label: "",
        data: [],
        noData: dataArr?.length === 0,
        borderColor: "transparent",
        backgroundColor: "transparent",
        pointRadius: 0,
      }];
    }

    return chartData;
  };

  // Sort the error columns properly based on integers not strings
  const errorPercentageSorting = (rowA, rowB) => {
    const intRowA = parseFloat(rowA?.total_fails_percent);
    const intRowB = parseFloat(rowB?.total_fails_percent);

    if (intRowA > intRowB) {
      return 1;
    }

    if (intRowB > intRowA) {
      return -1;
    }

    return 0;
  };

  const timeAgo = (timestamp, referenceTimestamp) => {
    if (!timestamp) return "";
    const pastDate = new Date(timestamp);

    // Measure "ago" from the most recent collection we have (the latest
    // timestamp in the dataset), not wall-clock now — otherwise a tag that was
    // active right up to the end of a historical range looks stale. Fall back
    // to "now" (in the client timezone) when no reference is available.
    const reference = referenceTimestamp
      ? new Date(referenceTimestamp)
      : new Date(new Date().toLocaleString("en-US", { timeZone: clientTimeStamp }));

    const timeDifference = reference - pastDate;
    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    // Beyond a week, "X days ago" stops being useful; show the absolute
    // last-fired timestamp instead.
    if (days > 7) {
      return String(timestamp).slice(0, 16);
    } else if (days > 0) {
      return `${days} day(s) and ${hours % 24} hour(s) ago`;
    } else if (hours > 0) {
      return `${hours} hour(s) and ${minutes % 60} minute(s) ago`;
    } else if (minutes > 0) {
      return `${minutes} minute(s) ago`;
    } else {
      return `${seconds} second(s) ago`;
    }
  };

  const CaretComponent = ({ row }) => {
    const handleClick = (event) => {
      event.stopPropagation();
      handleSnapshotAreaUpdate(row);
    };

    const isExpanded = snapshotToReview === row && snapshotExpanded;

    return (
      <div onClick={handleClick}>
        {isExpanded ? <CaretUpFill size={14} /> : <CaretDownFill size={14} />}
      </div>
    );
  };

    // Latest collection across the current tag-volume data; used as the "now"
    // reference for the Last-time-collected column so freshness is relative to
    // the data window, not wall-clock time.
    const lastCollectedReference = useMemo(() => {
        const rows = [...(tagVolClientData || []), ...(tagVolServerData || [])];
        let bestStr = null;
        let bestTime = -Infinity;
        rows.forEach((r) => {
            if (!r?.last_timestamp) return;
            const t = new Date(r.last_timestamp).getTime();
            if (!Number.isNaN(t) && t > bestTime) {
                bestTime = t;
                bestStr = r.last_timestamp;
            }
        });
        return bestStr;
    }, [tagVolClientData, tagVolServerData]);

    // Tables
    const tagVolumeTableColumns = useMemo(() => [
        {
            title: 'Tag name',
            dataIndex: 'tag_name',
            key: 'tag_name',
            width: '23%',
            render: (text, record) => (
                <span className={isTagExcluded(record) ? 'excluded-tag' : ''}>
                    {record?.tag_name && record?.tag_name !== "undefined" && record.tag_name}
                    
                    {(!record?.tag_name || record?.tag_name === "undefined") && (
                      <AntdTooltip title="Configure tag names" placement="right">
                        <a href="/authorize_user">
                          {record?.tag_id}
                        </a>
                      </AntdTooltip>
                    )}

                    {isTagExcluded(record) && (
                        <>
                            <StopOutlined className="excluded-icon" />
                            <span className="excluded-text">excluded</span>
                        </>
                    )}
                </span>
            ),
            sorter: (a, b) => {
                const aName = a?.tag_name !== "undefined" && a?.tag_name ? a?.tag_name : a?.tag_id;
                const bName = b?.tag_name !== "undefined" && b?.tag_name ? b?.tag_name : b?.tag_id;
                return (aName || '').localeCompare(bName || '');
            },
        },
        {
            title: 'Event name',
            dataIndex: 'event_name',
            key: 'event_name',
            width: '12%',
            sorter: (a, b) => (a.event_name || '').localeCompare(b.event_name || ''),
        },
        {
            title: 'Container ID',
            dataIndex: 'container_id',
            key: 'container_id',
            width: '12%',
            sorter: (a, b) => (a.container_id || '').localeCompare(b.container_id || ''),
        },
        {
            title: 'Version ID',
            dataIndex: 'container_version',
            key: 'container_version',
            width: '8%',
            sorter: (a, b) => {
                const valA = parseInt(a.container_version) || 0;
                const valB = parseInt(b.container_version) || 0;
                return valA - valB;
            },
        },
        {
            title: (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Last time collected
                    <AntdTooltip title="Time since this tag last fired, measured against the most recent data in the date range you're viewing, not the current time.">
                        <InfoCircleFilled
                            size={15}
                            className="info-icon"
                            style={{ marginLeft: "10px" }}
                        />
                    </AntdTooltip>
                </span>
            ),
            label: 'Last time collected',
            dataIndex: 'last_timestamp',
            key: 'last_timestamp',
            width: '14%',
            render: (text) => timeAgo(text, lastCollectedReference),
            sorter: (a, b) => {
                const dateA = new Date(a.last_timestamp);
                const dateB = new Date(b.last_timestamp);
                return dateA - dateB;
            },
        },
        {
            title: (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Calls
                    <AntdTooltip title="Number of tag calls collected based on sampling rate. This does not represent all calls, only a statistical sample.">
                        <InfoCircleFilled
                            size={15}
                            className="info-icon"
                            style={{ marginLeft: "10px" }}
                        />
                    </AntdTooltip>
                </span>
            ),
            dataIndex: 'total_calls',
            key: 'total_calls',
            width: '12%',
            render: (text) => formatNumber(text),
            sorter: (a, b) => (a.total_calls || 0) - (b.total_calls || 0),
            hideCheckbox: true,
        },
        {
            title: getComparisonPeriodText(),
            dataIndex: 'perc_last_hour',
            key: 'perc_last_hour',
            width: '19%',
            render: (text) => {
                const value = parseFloat(text);
                const isPositive = value > 0;
                const isNegative = value < 0;
                const isSignificantDrop = value < -50; // Only red if drop is more than 50%
                
                // Color logic: green for positive, red only for drops > 50%, otherwise gray
                const color = isPositive ? '#00A359' : isSignificantDrop ? '#F62064' : '#1f1f1f';
                
                return (
                    <div style={{ 
                        overflowX: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        color: color,
                        // fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        {isPositive && <CaretUpFill size={12} />}
                        {isNegative && <CaretDownFill size={12} />}
                        {isPositive && '+'}
                        {text}%
                    </div>
                );
            },
            sorter: (a, b) => parseFloat(a.perc_last_hour || 0) - parseFloat(b.perc_last_hour || 0),
            hideCheckbox: true,
        }
    ], [differenceOneDayOrOneHour, startDate, endDate, isTagExcluded, lastCollectedReference]);

    const errorTableColumns = [
        {
            title: 'Tag name',
            dataIndex: 'tag_name',
            key: 'tag_name',
            width: '17%',
            render: (text, record) => (
              <span
                className={isTagExcluded(record) ? 'excluded-tag' : ''}
                onClick={() => rowClickFunction(record)}
              >
                {record?.tag_name && record?.tag_name !== "undefined" && record.tag_name}

                {(!record?.tag_name || record?.tag_name === "undefined") && (
                  <AntdTooltip title="Configure tag names" placement="right">
                    <a href="/authorize_user">
                      {record?.tag_id}
                    </a>
                  </AntdTooltip>
                )}

                {isTagExcluded(record) && (
                  <>
                    <StopOutlined className="excluded-icon" />
                    <span className="excluded-text">excluded</span>
                  </>
                )}

              </span>
            ),
            sorter: (a, b) => {
                const aName = a?.tag_name !== "undefined" && a?.tag_name ? a?.tag_name : a?.tag_id;
                const bName = b?.tag_name !== "undefined" && b?.tag_name ? b?.tag_name : b?.tag_id;
                return (aName || '').localeCompare(bName || '');
            },
        },
        {
            title: 'Event name',
            dataIndex: 'event_name',
            key: 'event_name',
            width: '12%',
            sorter: (a, b) => (a.event_name || '').localeCompare(b.event_name || ''),
        },
        {
            title: 'Container ID',
            dataIndex: 'container_id',
            key: 'container_id',
            width: '11%',
            sorter: (a, b) => (a.container_id || '').localeCompare(b.container_id || ''),
        },
        {
            title: 'Version ID',
            dataIndex: 'container_version',
            key: 'container_version',
            width: '8%',
            sorter: (a, b) => {
                const valA = parseInt(a.container_version) || 0;
                const valB = parseInt(b.container_version) || 0;
                return valA - valB;
            },
        },
        {
            title: 'Hostname',
            dataIndex: 'hostname',
            key: 'hostname',
            width: '13%',
            render: (text, record) => record.market || text || '-',
            sorter: (a, b) =>
                ((a.market || a.hostname) || '').localeCompare(
                    (b.market || b.hostname) || '',
                ),
        },
        {
            name: 'Total calls',
            title: (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Calls
                    <AntdTooltip title="Number of tag calls collected based on sampling rate. This does not represent all calls, only a statistical sample.">
                        <InfoCircleFilled
                            size={15}
                            className="info-icon"
                            style={{ marginLeft: "10px" }}
                        />
                    </AntdTooltip>
                </span>
            ),
            dataIndex: 'total_calls',
            key: 'total_calls',
            width: '9%',
            render: (text) => formatNumber(text),
            sorter: (a, b) => (a.total_calls || 0) - (b.total_calls || 0),
            hideCheckbox: true,
        },
        {
            title: 'Error %',
            dataIndex: 'total_fails_percent',
            key: 'total_fails_percent',
            width: '8%',
            sorter: errorPercentageSorting,
            hideCheckbox: true,
        },
        // {
        //     title: 'Error %',
        //     dataIndex: 'total_fails_percent',
        //     key: 'total_fails_percent_bar',
        //     width: '8%',
        //     hideCheckbox: true,
        //     render: (text) => (
        //         <div className="error-column">
        //             <div className="error-bar" style={{ width: `${text}%` }}></div>
        //         </div>
        //     ),
        // }

    ];

  const diagnosisSeverityLabels = { high: 'High', medium: 'Medium', low: 'Low' };

  const diagnosisColumnHint =
    "Modeled from this tag's historical errors, so debugging starts from a " +
    'lead rather than a blank page. Open a row for the plausible cause and ' +
    'the first checks to run. Verify before acting.';

  const findDiagnosisForRow = (record) => {
    if (!record) return null;

    // Only show a diagnosis on rows that failed.
    if (Number(record.total_fails) === 0) return null;

    const sourceRows = record.sourceRows;
    if (Array.isArray(sourceRows) && sourceRows.length > 0) {
      const mergedKeys = new Set();
      sourceRows.forEach((row) => {
        mergedKeys.add(diagnosisKey(row.tag_id, row.container_id));
      });
      if (mergedKeys.size > 1) return null;
    }

    const key = diagnosisKey(record.tag_id, record.container_id);
    return diagnosticsByTag[key] || null;
  };

  const openDiagnosisDrawer = (event, diagnosis, rowKey) => {
    // Stop the row click, which drills the dashboard down to this tag.
    event.stopPropagation();
    diagnosisTriggerRef.current = event.currentTarget;
    setOpenDiagnosis(diagnosis);
    setOpenDiagnosisRowKey(rowKey);
    setDiagnosisDrawerOpen(true);
  };

  const closeDiagnosisDrawer = () => {
    setDiagnosisDrawerOpen(false);
    setOpenDiagnosisRowKey(null);
    if (diagnosisTriggerRef.current) {
      diagnosisTriggerRef.current.focus();
      diagnosisTriggerRef.current = null;
    }
  };

  const diagnosisColumn = {
    // The column is unlike its neighbours: everything else here is measured,
    // this is inferred. Say so on the header rather than only inside the drawer.
    title: (
      <span className="diagnosis-column-title">
        Tag Diagnostics
        <AntdTooltip title={diagnosisColumnHint}>
          <span
            className="diagnosis-hint"
            tabIndex={0}
            aria-label={diagnosisColumnHint}
            // The header is a sort control, so the icon must not sort as well.
            onClick={(event) => event.stopPropagation()}
          >
            {/* Same icon and .info-icon treatment as the Calls column. */}
            <InfoCircleFilled size={15} className="info-icon" />
          </span>
        </AntdTooltip>
      </span>
    ),
    key: 'tag_diagnostics',
    width: '14%',
    className: 'diagnosis-column',
    hideCheckbox: true,
    render: (text, record) => {
      const diagnosis = findDiagnosisForRow(record);
      if (!diagnosis) {
        return <span className="diagnosis-none">— No diagnosis</span>;
      }

      const severity = diagnosis.severity || 'low';
      const label = diagnosisSeverityLabels[severity] || 'Low';
      const isRegression = diagnosis.trend === 'regression';

      return (
        <button
          type="button"
          className="diagnosis-cell"
          onClick={(event) => openDiagnosisDrawer(event, diagnosis, record.key)}
          aria-label={`Open diagnostics for ${diagnosis.tag_name || diagnosis.tag_id}`}
        >
          <span className={`diagnosis-chip diagnosis-chip-${severity}`}>
            <span className="diagnosis-dot" />
            {label}
          </span>
          <span
            className={`diagnosis-trend-mark ${isRegression ? 'is-regression' : 'is-chronic'}`}
            title={isRegression ? 'Started failing recently' : 'Long-running issue'}
          >
            {isRegression ? '▲' : ''}
          </span>
          {/* <span className="diagnosis-view">View →</span> */}
        </button>
      );
    },
    sorter: (a, b) => {
      const aDiagnosis = findDiagnosisForRow(a);
      const bDiagnosis = findDiagnosisForRow(b);
      const aRank = aDiagnosis ? diagnosisSeverityRank[aDiagnosis.severity] || 0 : 0;
      const bRank = bDiagnosis ? diagnosisSeverityRank[bDiagnosis.severity] || 0 : 0;
      return aRank - bRank;
    },
  };

  
  const hasAnyDiagnosis = Object.keys(diagnosticsByTag).length > 0;
  const clientErrorTableColumns = hasAnyDiagnosis
    ? [...errorTableColumns, diagnosisColumn]
    : errorTableColumns;

  const severityColorMap = { critical: 'red', warning: 'orange', info: 'blue', normal: 'default' };
  const anomalyTypeColorMap = { spike: 'volcano', drop: 'cyan', 'spike & drop': 'purple', normal: 'default' };

  // "Needs attention" banner: counts tags (rows, not anomaly hours) bucketed by
  // worst severity.
  const renderAnomalySeveritySummary = (data = [], barKey) => {
    const counts = { critical: 0, warning: 0, info: 0 };
    data.forEach((row) => {
      if ((row?.anomalyCount || 0) <= 0) return;
      const sev = row?.worstSeverity;
      if (counts[sev] !== undefined) counts[sev] += 1;
    });
    const total = counts.critical + counts.warning + counts.info;
    const bg = '#fafafa';
    const border = counts.critical > 0 ? '#ffccc7'
      : counts.warning > 0 ? '#ffe58f'
      : total > 0 ? '#bae0ff' : '#b7eb8f';
    const accent = counts.critical > 0 ? '#cf1322'
      : counts.warning > 0 ? '#d46b08'
      : total > 0 ? '#0958d9' : '#389e0d';

    if (attentionBarCollapsed[barKey]) {
      return (
        <button
          type="button"
          aria-label="Show attention summary"
          title="Show attention summary"
          onClick={() =>
            setAttentionBarCollapsed((prev) => ({ ...prev, [barKey]: false }))
          }
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, padding: 0, background: bg,
            border: `1px solid ${border}`, borderRadius: '25%', cursor: 'pointer',
          }}
        >
          {total === 0 ? (
            <CheckCircleFilled style={{ color: accent, fontSize: 16 }} />
          ) : (
            <WarningFilled style={{ color: accent, fontSize: 16 }} />
          )}
        </button>
      );
    }

    const closeButton = (
      <button
        type="button"
        aria-label="Collapse"
        onClick={() =>
          setAttentionBarCollapsed((prev) => ({ ...prev, [barKey]: true }))
        }
        style={{
          marginLeft: 'auto', display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', width: 24, height: 24, padding: 0,
          border: 'none', background: 'transparent', borderRadius: 4,
          cursor: 'pointer', color: '#8c8c8c', lineHeight: 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#595959'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#8c8c8c'; }}
      >
        <CloseOutlined style={{ fontSize: 12 }} />
      </button>
    );

    return (
      <div
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'nowrap',
          padding: '10px 14px', maxWidth: '100%', minWidth: 'max-content', flexShrink: 0,
          background: bg, border: `1px solid ${border}`, borderRadius: 8,
        }}
      >
        {total === 0 ? (
          <>
            <CheckCircleFilled style={{ color: accent, fontSize: 16 }} />
            <span style={{ color: accent, fontWeight: 600 }}>
              No tags need attention in the selected range
            </span>
            {closeButton}
          </>
        ) : (
          <>
            <WarningFilled style={{ color: accent, fontSize: 16 }} />
            <span style={{ fontWeight: 600, color: accent }}>Anomaly severity</span>
            <AntdTooltip
              title={
                <div style={{ maxWidth: 260, fontSize: 12, lineHeight: 1.5 }}>
                  <b>How is severity determined?</b> <br />
                  Alerts trigger when a tag's volume goes outside its expected range:
                  <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
                    <li><strong>Critical:</strong> Way over (+900%) or way under (-90%)</li>
                    <li><strong>Warning:</strong> Highly over (+200%) or highly under (-60%)</li>
                    <li><strong>Info:</strong> Minor variations outside the expected range</li>
                  </ul>
                </div>
              }
            >
              <InfoCircleFilled
                style={{ color: accent, fontSize: 13, cursor: 'help' }}
              />
            </AntdTooltip>
            {counts.critical > 0 && <Tag color="red">{counts.critical} critical</Tag>}
            {counts.warning > 0 && <Tag color="orange">{counts.warning} warning</Tag>}
            {counts.info > 0 && <Tag color="blue">{counts.info} info</Tag>}
            {closeButton}
          </>
        )}
      </div>
    );
  };

  const anomalyTableColumns = [
    {
      title: 'Tag name',
      dataIndex: 'tagname',
      key: 'tagname',
      width: '25%',
      sorter: (a, b) =>
        (a?.tagname || a?.tagid || '').localeCompare(b?.tagname || b?.tagid || ''),
      render: (text, record) => record?.tagname || record?.tagid || '-',
    },
    {
      title: 'Container ID',
      dataIndex: 'containerid',
      key: 'containerid',
      width: '15%',
      sorter: (a, b) => (a?.containerid || '').localeCompare(b?.containerid || ''),
    },
    {
      title: 'Hostname',
      dataIndex: 'hostname',
      key: 'hostname',
      width: '20%',
      sorter: (a, b) => (a?.hostname || '').localeCompare(b?.hostname || ''),
    },
    {
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          Anomalies
          <AntdTooltip title="This number represents the amount of anomalies detected for a tag in the selected timeframe">
            <InfoCircleFilled
              size={15}
              className="info-icon"
              style={{ marginLeft: '10px' }}
            />
          </AntdTooltip>
        </span>
      ),
      label: 'Anomalies',
      dataIndex: 'anomalyCount',
      key: 'anomalyCount',
      width: '12%',
      hideCheckbox: true,
      sorter: (a, b) => (a?.anomalyCount || 0) - (b?.anomalyCount || 0),
      render: (text, record) => {
        const count = record?.anomalyCount || 0;
        if (count <= 0) return <span style={{ color: '#b0b0b0' }}>0</span>;
        // Colour the count by the tag's worst severity so a big number of trivial
        // spikes doesn't look as alarming as a single critical drop.
        const sev = record?.worstSeverity || 'info';
        return <Tag color={severityColorMap[sev] || 'red'}>{count}</Tag>;
      },
    },
    {
      title: 'Current count',
      dataIndex: 'tagfiredcountlog',
      key: 'tagfiredcountlog',
      width: '13%',
      hideCheckbox: true,
      render: (text) => Math.round(text || 0),
      sorter: (a, b) => (a?.tagfiredcountlog || 0) - (b?.tagfiredcountlog || 0),
    },
    {
      title: 'Expected range',
      key: 'expectedrange',
      width: '15%',
      hideCheckbox: true,
      // Sort by the lower bound (the leading number shown in the range).
      sorter: (a, b) => (a?.yhatlower || 0) - (b?.yhatlower || 0),
      render: (_, record) => {
        const lower = Math.round(record?.yhatlower || 0);
        const upper = Math.round(record?.yhatupper || 0);
        return `${lower} - ${upper}`;
      },
    },
  ];

  const cloudRunTableColumns = [
    {
      name: "Date / Time",
      sortable: true,
      width: "10%",
      selector: (row) => row?.formatted_timestamp || "-",
    },
    {
      name: "Project ID",
      sortable: true,
      width: "10%",
      selector: (row) => row?.project_id || "-",
    },
    {
      name: "Service Name",
      sortable: true,
      width: "18%",
      selector: (row) => row?.service_name || "-",
    },
    {
      name: "Summary",
      sortable: true,
      width: "58%",
      selector: (row) => row?.summary || "-",
      cell: (row) => (
        <AntdTooltip title={row?.summary}>
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {row?.summary}
          </div>
        </AntdTooltip>
      ),
    },
    {
      name: "",
      width: "1%",
      cell: (row) => <CaretComponent row={row} />,
    },
  ];

  const consentMonitoringTableColumns = [
    {
      title: 'Tag name',
      dataIndex: 'tag_name',
      key: 'tag_name',
      width: '17%',
      render: (text, record) => (
        <span
          className={isTagExcluded(record) ? 'excluded-tag' : ''}
          onClick={() => rowClickFunction(record)}
        >
          {record?.tag_name !== "undefined" && record?.tag_name ? record?.tag_name : record?.tag_id}
          {isTagExcluded(record) && (
            <>
              <StopOutlined className="excluded-icon" />
              <span className="excluded-text">excluded</span>
            </>
          )}
        </span>
      ),
      sorter: (a, b) => {
        const aName = a?.tag_name !== "undefined" && a?.tag_name ? a?.tag_name : a?.tag_id;
        const bName = b?.tag_name !== "undefined" && b?.tag_name ? b?.tag_name : b?.tag_id;
        return (aName || '').localeCompare(bName || '');
      },
    },
    {
      title: "Consent Type",
      dataIndex: "consent_name",
      key: "consent_name",
      width: "14%",
      render: (text) => text || "-",
      hideCheckbox: true,
    },
    {
      title: "Container ID",
      dataIndex: "container_id",
      key: "container_id",
      width: "14%",
      render: (text) => text || "-",
      sorter: (a, b) => (a.container_id || '').localeCompare(b.container_id || ''),
    },
    {
      title: "Hostname",
      dataIndex: "hostname",
      key: "hostname",
      width: "16%",
      render: (text, record) => record.market || text || "-",
      sorter: (a, b) =>
        ((a.market || a.hostname) || "").localeCompare(
          (b.market || b.hostname) || "",
        ),
    },
    {
      title: "Event name",
      dataIndex: "event_name",
      key: "event_name",
      width: "14%",
      render: (text) => text || "-",
      sorter: (a, b) => (a.event_name || '').localeCompare(b.event_name || ''),
    },
    {
      title: "Tags collected",
      dataIndex: "total_values",
      key: "total_values",
      width: "12%",
      render: (text) => text || "0",
      sorter: (a, b) => (parseInt(a.total_values) || 0) - (parseInt(b.total_values) || 0),
      hideCheckbox: true,
    },
    {
      title: "Denied pageview events",
      dataIndex: "denied",
      key: "denied",
      width: "14%",
      render: (text) => text || "0",
      sorter: (a, b) => (parseInt(a.denied) || 0) - (parseInt(b.denied) || 0),
      hideCheckbox: true,
    },
    {
      title: "Approval in %",
      dataIndex: "approval_percent",
      key: "approval_percent",
      width: "12%",
      render: (text) => (text ? `${text}%` : "0%"),
      sorter: (a, b) => {
        const valA = parseFloat(a?.approval_percent) || 0;
        const valB = parseFloat(b?.approval_percent) || 0;
        return valA - valB;
      },
      hideCheckbox: true,
    },
  ];

  const sitePerformanceTableColumns =
    sitePerfGroupOption === "tag_name"
      ? [
          {
            name: "Tag Name",
            sortable: true,
            width: "50%",
            selector: (row) => {
              if (!row) return "-";
              return row?.tag_name !== "undefined" && row?.tag_name
                ? row?.tag_name
                : row?.tag_id || "-";
            },
            cell: (row) => {
              if (!row) return "-";
              const name = row?.tag_name && row?.tag_name !== "undefined"
                ? row.tag_name
                : "";
              return (
                <span className={isTagExcluded(row) ? 'excluded-tag' : ''}>
                  
                  {(!row?.tag_name || row?.tag_name === "undefined") && (
                    <AntdTooltip title="Configure tag names" placement="right">
                      <a href="/authorize_user">   
                        {row?.tag_id}
                      </a>
                    </AntdTooltip>
                  )}

                  {name}

                  {isTagExcluded(row) && (
                    <>
                      <StopOutlined className="excluded-icon" />
                      <span className="excluded-text">excluded</span>
                    </>
                  )}
                </span>
              );
            },
          },
          {
            name: "Average execution time (ms)",
            sortable: true,
            width: "45%",
            selector: (row) => row?.avg_ex_time || "0",
          },
        ]
      : [
          {
            name: "Event Name",
            sortable: true,
            width: "50%",
            selector: (row) => {
              if (!row) return "-";
              return row?.event_name !== "undefined" && row?.event_name
                ? row?.event_name
                : row?.tag_id || "-";
            },
          },
          {
            name: "Average execution time (ms)",
            sortable: true,
            width: "45%",
            selector: (row) => row?.avg_ex_time || "0",
          },
        ];

  const aggregateErrorsByTagName = (data = []) => {
    // Create an object to store the aggregated data
    const aggregatedData = {};

    data.forEach((item) => {
      const {
        tag_id,
        tag_name,
        total_fails,
        total_calls,
        container_id,
        event_name,
        hostname,
        url,
        market,
        container_version,
      } = item;
      // Include market in the key so rows for /de and /dk don't collapse
      // into one bucket. market may be null for clients without path entries.
      const key = `${tag_id}-${container_id}-${event_name}-${market ?? ""}-${container_version}-${hostname}`;

      // Check if the tag_name already exists in the aggregatedData object
      if (!aggregatedData[key]) {
        aggregatedData[key] = {
          key: key, // Add key for Ant Design Table
          tag_id: tag_id,
          tag_name: tag_name,
          container_id: container_id,
          event_name: event_name,
          hostname: hostname || "", // Make sure hostname is included
          url: url || "", // Keep representative full URL when available
          market: market || null, // Carry market so the Hostname column can render it
          container_version: container_version, // Make sure container_version is included
          total_fails: 0,
          total_calls: 0,
          total_fails_percent: 0,
        };
      }

      aggregatedData[key].total_fails += total_fails;
      aggregatedData[key].total_calls += total_calls;
      aggregatedData[key].total_fails_percent = (
        (aggregatedData[key].total_fails / aggregatedData[key].total_calls) *
        100
      ).toFixed(2);

      // Maintain the most recent URL, hostname and container_version if they're different
      if (item.hostname && item.hostname !== aggregatedData[key].hostname) {
        aggregatedData[key].hostname = item.hostname;
      }
      if (item.url && item.url !== aggregatedData[key].url) {
        const currentUrl = aggregatedData[key].url || "";
        const currentHasPath = /^https?:\/\/[^/]+\/./i.test(currentUrl);
        const nextHasPath = /^https?:\/\/[^/]+\/./i.test(item.url);
        if (!currentUrl || (!currentHasPath && nextHasPath)) {
          aggregatedData[key].url = item.url;
        }
      }
      if (item.market && item.market !== aggregatedData[key].market) {
        aggregatedData[key].market = item.market;
      }
      if (
        item.container_version &&
        item.container_version !== aggregatedData[key].container_version
      ) {
        aggregatedData[key].container_version = item.container_version;
      }
    });

    // Convert the aggregatedData object into an array of objects
    return Object.values(aggregatedData).sort(
      (a, b) => b.total_fails_percent - a.total_fails_percent,
    );
  };

  // Add transformTableData function definition
  const transformTableData = (data) => {
    if (!Array.isArray(data)) return [];
    return data.map((item, index) => ({
      key: item.id || index,
      ...item,
    }));
  };

  const severityRank = { critical: 3, warning: 2, info: 1, normal: 0 };

  // Anomalous rows on top, ordered by worst severity (critical → warning → info)
  // then anomaly count; non-anomalous rows keep their relative order below.
  const compareAnomalyRows = (a, b) => {
    const aAnom = (a.anomalyCount || 0) > 0;
    const bAnom = (b.anomalyCount || 0) > 0;
    if (aAnom !== bAnom) return aAnom ? -1 : 1;
    const sevDiff = (severityRank[b.worstSeverity] || 0) - (severityRank[a.worstSeverity] || 0);
    return sevDiff !== 0 ? sevDiff : (b.anomalyCount || 0) - (a.anomalyCount || 0);
  };

  const deriveAnomalyAggregates = (entry) => {
    const anomalyRows = entry.sourceRows.filter((r) => Number(r.anomaly) !== 0);
    let worstSeverity = 'normal';
    anomalyRows.forEach((r) => {
      const sev = r.severity || 'normal';
      if ((severityRank[sev] || 0) > (severityRank[worstSeverity] || 0)) {
        worstSeverity = sev;
      }
    });
    const types = new Set(
      anomalyRows.map((r) => r.anomalytype).filter((t) => t && t !== 'normal'),
    );
    let anomalyType = 'normal';
    if (types.has('spike') && types.has('drop')) anomalyType = 'spike & drop';
    else if (types.has('spike')) anomalyType = 'spike';
    else if (types.has('drop')) anomalyType = 'drop';
    // Calculate deviation relative to the expected range boundaries
    // Above upper → positive %, Below lower → negative %, In range → 0
    let maxDeviation = null;
    entry.sourceRows.forEach((r) => {
      // Use actual_* (full-precision inverse of log1p from SQL), not the truncated
      // count_in_last_hour; Number() keeps NaN so the guard below still works.
      const count = Number(r.actual_count);
      const lower = Number(r.actual_yhat_lower);
      const upper = Number(r.actual_yhat_upper);
      if (isNaN(count) || isNaN(lower) || isNaN(upper)) return;
      let dev = 0;
      if (count > upper && upper > 0) {
        dev = ((count - upper) / upper) * 100;
      } else if (count < lower && lower > 0) {
        dev = ((count - lower) / lower) * 100;
      }
      if (maxDeviation === null || Math.abs(dev) > Math.abs(maxDeviation)) {
        maxDeviation = dev;
      }
    });
    return { worstSeverity, anomalyType, maxDeviation };
  };

  const aggregateAnomalyByTagName = (data = []) => {
    const aggregated = {};

    data.forEach((item) => {
      const key = `${item.tagid || ''}-${item.containerid || ''}-${item.hostname || ''}`;

      if (!aggregated[key]) {
        aggregated[key] = {
          key: key,
          tagid: item.tagid,
          tagname: item.tagname,
          containerid: item.containerid,
          hostname: item.hostname,
          tagfiredcountlog: 0,
          yhatlower: 0,
          yhatupper: 0,
          anomalyCount: 0,
          totalPoints: 0,
          sourceRows: [],
        };
      }

      // actual_* are already real counts (SQL exp'd them per row), so sum directly
      aggregated[key].tagfiredcountlog += item.actual_count || 0;
      aggregated[key].yhatlower += item.actual_yhat_lower || 0;
      aggregated[key].yhatupper += item.actual_yhat_upper || 0;
      if (isRowAnomalous(item)) aggregated[key].anomalyCount++;
      aggregated[key].totalPoints++;
      aggregated[key].sourceRows.push(item);
    });

    return Object.values(aggregated)
      .map((entry) => ({
        ...entry,
        ...deriveAnomalyAggregates(entry),
      }))
      .sort(compareAnomalyRows);
  };

  const aggregateErrorsSitePerformanceByTagNameData = (data = []) => {
    // Create an object to store the aggregated data
    const aggregatedData = {};
    data.forEach((item) => {
      const { tag_id, tag_name, container_id, avg_ex_time } = item;
      const key = `${tag_id}-${container_id}-${tag_name}`;
      // const key = `${tag_id}-${container_id}-${tag_name}-${container_version}-${hostname}`;

      // Check if the tag_name already exists in the aggregatedData object
      if (!aggregatedData[key]) {
        aggregatedData[key] = {
          key: key, // Add key for Ant Design Table
          tag_id: tag_id,
          tag_name: tag_name,
          container_id: container_id,
          // hostname: hostname || "",
          // container_version: container_version, // Make sure container_version is included
          avg_ex_time: 0,
        };
      }

      aggregatedData[key].avg_ex_time += avg_ex_time;
    });

    // Convert the aggregatedData object into an array of objects
    return Object.values(aggregatedData)
      .map((item) => ({
        ...item,
        avg_ex_time: Math.round(item.avg_ex_time),
      }))
      .sort((a, b) => b.avg_ex_time - a.avg_ex_time);
  };

  const aggregateErrorsSitePerformanceByEventNameData = (data = []) => {
    // Create an object to store the aggregated data
    const aggregatedData = {};

    data.forEach((item) => {
      const { tag_id, event_name, container_id, avg_ex_time } = item;
      const key = `${tag_id}-${container_id}-${event_name}`;

      // Check if the tag_name already exists in the aggregatedData object
      if (!aggregatedData[key]) {
        aggregatedData[key] = {
          key: key, // Add key for Ant Design Table
          tag_id: tag_id,
          container_id: container_id,
          event_name: event_name,
          avg_ex_time: 0,
        };
      }

      aggregatedData[key].avg_ex_time += avg_ex_time;
    });

    // Convert the aggregatedData object into an array of objects
    return Object.values(aggregatedData)
      .map((item) => ({
        ...item,
        avg_ex_time: Math.round(item.avg_ex_time),
      }))
      .sort((a, b) => b.avg_ex_time - a.avg_ex_time);
  };

  const aggregateByVisibleColumns = (data, visibleColumns) => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    const aggregated = {};

    data.forEach((item) => {
      const keyParts = [];

      if (visibleColumns.tag_name) {
        keyParts.push(`tag:${item.tag_id || ""}:${item.tag_name || ""}`);
      }
      if (visibleColumns.event_name) {
        keyParts.push(`event:${item.event_name || ""}`);
      }
      if (visibleColumns.container_id) {
        keyParts.push(`container:${item.container_id || ""}`);
      }
      if (visibleColumns.container_version) {
        keyParts.push(`version:${item.container_version || ""}`);
      }
      if (visibleColumns.hostname) {
        keyParts.push(`host:${item.hostname || ""}`);
      }

      const key = keyParts.join("|||");

      if (!aggregated[key]) {
        aggregated[key] = {
          ...item,
          key,
          total_calls: 0,
          total_fails: 0,
          sourceRows: [],
        };
      }

      // Sum the correct totals (use merged fields)
      aggregated[key].total_calls += Number(item.total_calls) || 0;
      aggregated[key].total_fails += Number(item.total_fails) || 0;
      aggregated[key].sourceRows.push(item);
    });

    // Now calculate the percent based on merged totals
    Object.values(aggregated).forEach((item) => {
      item.total_fails_percent =
        item.total_calls > 0
          ? ((item.total_fails / item.total_calls) * 100).toFixed(2)
          : "0.00";
    });

    // Sorting by error % descending
    return Object.values(aggregated).sort(
      (a, b) =>
        parseFloat(b.total_fails_percent) - parseFloat(a.total_fails_percent),
    );
  };

  // Add these useMemo hooks before the return statement
  const aggregatedClientErrorsTable = useMemo(() => {
    // Don't aggregate if visibility hasn't been set yet - use original data
    if (!visibleErrorColumns) return clientErrorsTable;

    // Check if ALL columns are visible - if so, don't aggregate
    const allVisible = Object.values(visibleErrorColumns).every(
      (v) => v === true,
    );
    if (allVisible) return clientErrorsTable;

    return aggregateByVisibleColumns(clientErrorsTable, visibleErrorColumns);
  }, [clientErrorsTable, visibleErrorColumns]);

  const aggregatedServerErrorsTable = useMemo(() => {
    if (!visibleErrorColumns) return serverErrorsTable;

    const allVisible = Object.values(visibleErrorColumns).every(
      (v) => v === true,
    );
    if (allVisible) return serverErrorsTable;

    return aggregateByVisibleColumns(serverErrorsTable, visibleErrorColumns);
  }, [serverErrorsTable, visibleErrorColumns]);

  const aggregatedTagVolClientData = useMemo(() => {
    if (!visibleTagVolumeColumns) return tagVolClientData;

    const allVisible = Object.values(visibleTagVolumeColumns).every(
      (v) => v === true,
    );
    if (allVisible) return tagVolClientData;

    // Simplified version for tag volume (no totalfails field)
    return aggregateByVisibleColumns(tagVolClientData, visibleTagVolumeColumns);
  }, [tagVolClientData, visibleTagVolumeColumns]);

  const aggregatedTagVolServerData = useMemo(() => {
    if (!visibleTagVolumeColumns) return tagVolServerData;

    const allVisible = Object.values(visibleTagVolumeColumns).every(
      (v) => v === true,
    );
    if (allVisible) return tagVolServerData;

    return aggregateByVisibleColumns(tagVolServerData, visibleTagVolumeColumns);
  }, [tagVolServerData, visibleTagVolumeColumns]);

  const aggregateAnomalyByVisibleColumns = (data, visibleColumns) => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const aggregated = {};

    data.forEach((item) => {
      const keyParts = [];
      if (visibleColumns.tagname) {
        keyParts.push(`tag:${item.tagid || ''}:${item.tagname || ''}`);
      }
      if (visibleColumns.containerid) {
        keyParts.push(`container:${item.containerid || ''}`);
      }
      if (visibleColumns.hostname) {
        keyParts.push(`host:${item.hostname || ''}`);
      }

      const key = keyParts.join('|||');

      if (!aggregated[key]) {
        aggregated[key] = {
          ...item,
          key,
          tagfiredcountlog: 0,
          yhatlower: 0,
          yhatupper: 0,
          anomalyCount: 0,
          totalPoints: 0,
          sourceRows: [],
        };
      }

      aggregated[key].tagfiredcountlog += item.tagfiredcountlog || 0;
      aggregated[key].yhatlower += item.yhatlower || 0;
      aggregated[key].yhatupper += item.yhatupper || 0;
      if (Number(item.anomaly) !== 0) aggregated[key].anomalyCount++;
      aggregated[key].totalPoints++;
      if (item.sourceRows) {
        aggregated[key].sourceRows.push(...item.sourceRows);
      } else {
        aggregated[key].sourceRows.push(item);
      }
    });

    return Object.values(aggregated)
      .map((entry) => ({
        ...entry,
        ...deriveAnomalyAggregates(entry),
      }))
      .sort(compareAnomalyRows);
  };

  const aggregatedAnomalyClientTable = useMemo(() => {
    let data = anomalyClientTable;
    if (anomalyOnlyFilter) {
      data = data.filter((row) => row.anomalyCount > 0);
    }

    if (!visibleAnomalyColumns) return data;

    const allVisible = Object.values(visibleAnomalyColumns).every(
      (v) => v === true,
    );
    if (allVisible) return data;

    return aggregateAnomalyByVisibleColumns(data, visibleAnomalyColumns);
  }, [anomalyClientTable, visibleAnomalyColumns, anomalyOnlyFilter]);

  const aggregatedAnomalyServerTable = useMemo(() => {
    let data = anomalyServerTable;
    if (anomalyOnlyFilter) {
      data = data.filter((row) => row.anomalyCount > 0);
    }

    if (!visibleAnomalyColumns) return data;

    const allVisible = Object.values(visibleAnomalyColumns).every(
      (v) => v === true,
    );
    if (allVisible) return data;

    return aggregateAnomalyByVisibleColumns(data, visibleAnomalyColumns);
  }, [anomalyServerTable, visibleAnomalyColumns, anomalyOnlyFilter]);

  // Re-expand the "Needs attention" bars whenever fresh anomaly data loads, so a
  // collapse only lasts for the current dataset (not across reloads).
  useEffect(() => {
    setAttentionBarCollapsed({ client: false, server: false });
  }, [anomalyClientTable, anomalyServerTable]);

  // Re-render anomaly charts when anomalyOnlyFilter changes
  useEffect(() => {
    if (!freshData.anomaly_data_client && !freshData.anomaly_data_server) return;

    const filterData = (data) => {
      if (!data) return data;
      return anomalyOnlyFilter
        ? data.filter(isRowAnomalous)
        : data;
    };

    const clientData = filterData(freshData.anomaly_data_client);
    const serverData = filterData(freshData.anomaly_data_server);

    setAnomalyClientData(
      renderLineChart({
        dataArr: clientData,
        aggrParam: "tagfiredcountlog",
        setLabelFunc: setAnomalyClientLabels,
        chartOptionFunc: setAnomalyClientOptions,
        chartOptionType: "anomaly-monitoring",
        chartElementsNum: chartEntriesNum,
        groupByOption: clientTagAnomalyGroupOption,
        aggregateAllTags: true,
      }),
    );

    setAnomalyServerData(
      renderLineChart({
        dataArr: serverData,
        aggrParam: "tagfiredcountlog",
        setLabelFunc: setAnomalyServerLabels,
        chartOptionFunc: setAnomalyServerOptions,
        chartOptionType: "anomaly-monitoring",
        chartElementsNum: chartEntriesNum,
        groupByOption: serverTagAnomalyGroupOption,
        aggregateAllTags: true,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anomalyOnlyFilter]);

  // Compute download button position so it sits above the 'Error %' header cell
  useEffect(() => {
    const computeDownloadBtnPos = () => {
      try {
        const wrapper = errorTableWrapperRef.current;
        const btn = downloadButtonRef.current;
        if (!wrapper || !btn) return;

        const ths = wrapper.querySelectorAll("table thead th");
        let targetTh = null;
        for (let i = 0; i < ths.length; i++) {
          const txt = (ths[i].textContent || "").trim();
          if (/Error\s*%/i.test(txt)) {
            targetTh = ths[i];
            break;
          }
        }

        if (!targetTh) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const thRect = targetTh.getBoundingClientRect();

        const btnWidth = 36;
        const left = Math.round(thRect.left - wrapperRect.left + thRect.width / 2 - btnWidth / 2);
        const top = Math.round(thRect.top - wrapperRect.top - 8);

        setDownloadBtnPos({ left, top });
      } catch (e) {
        // ignore
      }
    };

    computeDownloadBtnPos();
    window.addEventListener("resize", computeDownloadBtnPos);
    return () => window.removeEventListener("resize", computeDownloadBtnPos);
  }, [visibleErrorColumns, aggregatedClientErrorsTable, tabsVisibility]);

  const exportTabLabels = {
    error_monitoring: "error-monitoring",
    tag_volume: "tag-volume",
    consent_monitoring: "consent-monitoring",
    site_performance: "site-performance",
    cloud_run: "cloud-run",
  };

  const exportTabDisplayLabels = {
    errors_client: "Error Monitoring",
    errors_server: "Error Monitoring",
    tag_volume_client: "Tag Volume",
    tag_volume_server: "Tag Volume",
    consent_monitoring: "Consent Monitoring",
    site_performance: "Site Performance",
    cloud_run: "Cloud Run Monitoring",
  };

  const exportColumnDefaults = {
    errors_client: [
      "tag_name",
      "tag_id",
      "event_name",
      "container_id",
      "container_version",
      "hostname",
      "url",
      "total_calls",
      "total_fails",
      "total_fails_percent",
    ],
    errors_server: [
      "tag_name",
      "tag_id",
      "event_name",
      "container_id",
      "container_version",
      "hostname",
      "url",
      "total_calls",
      "total_fails",
      "total_fails_percent",
    ],
    tag_volume_client: [
      "tag_name",
      "tag_id",
      "event_name",
      "container_id",
      "container_version",
      "hostname",
      "url",
      "last_timestamp",
      "total_calls",
      "perc_last_hour",
    ],
    tag_volume_server: [
      "tag_name",
      "tag_id",
      "event_name",
      "container_id",
      "container_version",
      "hostname",
      "url",
      "last_timestamp",
      "total_calls",
      "perc_last_hour",
    ],
    consent_monitoring: [
      "tag_name",
      "consent_name",
      "container_id",
      "hostname",
      "url",
      "event_name",
      "granted",
      "denied",
      "total_values",
      "approval_percent",
    ],
    site_performance: sitePerfGroupOption === "tag_name"
      ? ["tag_name", "tag_id", "container_id", "avg_ex_time"]
      : ["event_name", "tag_id", "container_id", "avg_ex_time"],
    cloud_run: [
      "formatted_timestamp",
      "project_id",
      "project_name",
      "service_name",
      "location",
      "observed_value",
      "threshold_value",
      "condition_name",
      "summary",
    ],
  };

  const exportViewTabMap = {
    client: [
      "errors_client",
      "tag_volume_client",
      "consent_monitoring",
      "site_performance",
    ],
    server: ["errors_server", "tag_volume_server", "cloud_run"],
  };

  const normalizeExportValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    if (Array.isArray(value)) {
      return value.map((item) => normalizeExportValue(item)).join(", ");
    }

    if (dayjs.isDayjs?.(value)) {
      return value.format("YYYY-MM-DD HH:mm:ss");
    }

    if (value instanceof Date) {
      return dayjs(value).format("YYYY-MM-DD HH:mm:ss");
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };

  const getExportTabRows = (tabKey) => {
    // Export from latest fetched data so current filters are respected.
    // Fallback to initialData only if freshData is not available yet.
    const sourceData = Object.keys(freshData || {}).length > 0 ? freshData : initialData;
    switch (tabKey) {
      case "errors_client": {
        const rawData = sourceData?.error_query_client || [];
        return aggregateErrorsByTagName(rawData);
      }
      case "errors_server": {
        const rawData = sourceData?.error_query_server || [];
        return aggregateErrorsByTagName(rawData);
      }
      case "tag_volume_client": {
        const rawData = sourceData?.tag_vol_query_client || [];
        return transformTableData(rawData);
      }
      case "tag_volume_server": {
        const rawData = sourceData?.tag_vol_query_server || [];
        return transformTableData(rawData);
      }
      case "consent_monitoring": {
        const rawData = (sourceData?.consent_monitoring || []).filter(
          matchesConsentType,
        );
        return transformTableData(aggregateConsentMonitoringData(rawData));
      }
      case "site_performance": {
        if (sitePerfGroupOption === "tag_name") {
          const rawData = sourceData?.site_performance_per_tag || [];
          return aggregateErrorsSitePerformanceByTagNameData(rawData);
        } else {
          const rawData = sourceData?.site_performance_per_trigger || [];
          return aggregateErrorsSitePerformanceByEventNameData(rawData);
        }
      }
      case "cloud_run": {
        const rawData = sourceData?.cloud_run || [];
        return transformTableData(rawData);
      }
      default:
        return [];
    }
  };

const exportColumnBlacklist = {
  errors_client: ["monitoring_view", "tab"],
  errors_server: ["monitoring_view", "tab"],
  tag_volume_client: ["monitoring_view", "tab", "data_type"],
  tag_volume_server: ["monitoring_view", "tab", "data_type"],
  consent_monitoring: ["monitoring_view", "tab"],
  site_performance: ["url"],
};

const getExportTabColumns = (tabKey, rows = []) => {
  const fallbackColumns = exportColumnDefaults[tabKey] || [];
  const blacklist = exportColumnBlacklist[tabKey] || [];
  const columns = [];

  const pushColumn = (columnName) => {
    if (
      !columnName ||
      columnName === "key" ||
      columnName === "sourceRows" ||
      blacklist.includes(columnName)
    ) {
      return;
    }

    if (!columns.includes(columnName)) {
      columns.push(columnName);
    }
  };

  fallbackColumns.forEach(pushColumn);
  rows.forEach((row) => Object.keys(row || {}).forEach(pushColumn));

  return columns;
};

  const getVisibleTabsForExport = () => exportViewTabMap[monitoringView] || [];

  // Map display tab names to export data keys
  const mapTabNameToExportKey = (tabName) => {
    switch (tabName) {
      case "error_monitoring":
        return `errors_${monitoringView}`;
      case "tag_volume":
        return `tag_volume_${monitoringView}`;
      case "consent_monitoring":
        return "consent_monitoring";
      case "site_performance":
        return "site_performance";
      case "cloud_run":
        return "cloud_run";
      default:
        return tabName;
    }
  };

  const buildExportMetadataLines = (scopeLabel, tabKey = null) => {
    // If no tabKey provided, use the current active tab mapped to export key
    if (!tabKey) {
      tabKey = mapTabNameToExportKey(activeTab);
    }
    const filtersSummary = [
      `Tags: ${selectedTags.length ? selectedTags.join(", ") : "All"}`,
      tabKey === "consent_monitoring" || scopeLabel === "all-tabs"
        ? `Consent types: ${chosenConsentType.length ? chosenConsentType.join(", ") : "All"}`
        : null,
      `Events: ${chosenEventName.length ? chosenEventName.join(", ") : "All"}`,
      `Events: ${chosenContainerVersion.length ? chosenContainerVersion.join(", ") : "All"}`,
      `Containers: ${chosenContainerId.length ? chosenContainerId.join(", ") : "All"}`,
      `Domains: ${chosenDomain.length ? chosenDomain.join(", ") : "All"}`,
      searchUrlValue ? `URL: ${searchUrlValue}` : null,
    ]
      .filter(Boolean)
      .join(" | ");

    const tabLabel =
      scopeLabel === "all-tabs"
        ? getVisibleTabsForExport()
            .map((tabName) => exportTabDisplayLabels[tabName] || tabName)
            .join(", ")
        : exportTabDisplayLabels[tabKey] || tabKey;

    const metadataLines = [
      `Generated: ${dayjs().format("YYYY-MM-DD HH:mm:ss")}`,
      `Company: ${companyInfo?.name || "N/A"}`,
      `Monitoring view: ${monitoringView}-side`,
      `Scope: ${scopeLabel === "all-tabs" ? "All tabs" : "Current tab"}`,
      `Tab(s): ${tabLabel}`,
      `Date range: ${startDate.format("YYYY-MM-DD")} to ${endDate.format("YYYY-MM-DD")}`,
      `Filters: ${filtersSummary}`,
    ];

    if (tabKey === "site_performance" || scopeLabel === "all-tabs") {
      metadataLines.push(
        `Site performance grouping: ${sitePerfGroupOption === "tag_name" ? "tag name" : "event name"}`,
      );
    }

    return metadataLines;
  };

  const buildExportRows = (tabKey, rows, scopeLabel) =>
    (rows || []).map((row) => {
      const rawUrl =
        row?.url ||
        row?.page_url ||
        row?.current_url ||
        row?.full_url ||
        "";
      const rawPath = row?.path || row?.pathname || row?.url_path || row?.page_path || "";
      const fallbackDomain = row?.hostname || row?.domain || "";

      let domain = fallbackDomain;
      let path = rawPath || "";
      let exportUrl = rawUrl || "";

      if (rawUrl) {
        const candidate = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
        try {
          const parsed = new URL(candidate);
          domain = parsed.hostname || domain;
          if (!path) {
            path = `${parsed.pathname || '/'}${parsed.search || ''}`;
          }
          exportUrl = `${parsed.protocol}//${parsed.host}${parsed.pathname || '/'}${parsed.search || ''}`;
        } catch {
          domain = domain || rawUrl;
        }
      } else if (domain && path) {
        exportUrl = `https://${domain}${path.startsWith('/') ? path : `/${path}`}`;
      } else if (domain) {
        exportUrl = `https://${domain}`;
      }

      return {
        ...(scopeLabel === "all-tabs"
          ? {
              monitoring_view: monitoringView,
              tab: exportTabDisplayLabels[tabKey] || tabKey,
            }
          : {
              monitoring_view: monitoringView,
              tab: exportTabDisplayLabels[tabKey] || tabKey,
            }),
        ...(row || {}),
        url: exportUrl || "",
      };
  });

  const buildCsvLine = (values = []) =>
    values
      .map((value) => {
        const normalized = normalizeExportValue(value).replace(/\r?\n/g, " ");
        if (/[",;]/.test(normalized)) {
          return `"${normalized.replace(/"/g, '""')}"`;
        }
        return normalized;
      })
      .join(",");

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Disclaimer constants
  const DISCLAIMER = 'This document contains a snapshot of your Tag Monitoring data as generated at the time of download. For the most up-to-date information, please log in to your account at:';
  const DISCLAIMER_URL = 'https://portal.code-cube.io';
  const DISCLAIMER_LINK_TEXT = 'portal.code-cube.io';

  const writeWorksheet = (worksheet, title, metadataLines, columns, rows) => {
    // Keep top section width in sync with the actual last data column.
    // Use exact columns.length so bands cut at the last data column.
    const topSectionColCount = Math.max(columns.length, 1);
    const lastTopSectionCol = colIndexToLetter(topSectionColCount);

    const writeDisclaimerRow = (rowIdx) => {
      const disclaimerCell = worksheet.getCell(`A${rowIdx}`);
      disclaimerCell.value = {
        richText: [
          { text: `Disclaimer: ${DISCLAIMER} `, font: { italic: true, size: 8, color: { argb: "FF999999" }, name: "Calibri" } },
          { text: DISCLAIMER_LINK_TEXT, hyperlink: DISCLAIMER_URL, font: { italic: true, size: 8, color: { argb: "FF999999" }, underline: true, name: "Calibri" } }
        ]
      };
      disclaimerCell.alignment = { wrapText: true, vertical: "middle", horizontal: "left", indent: 4 };
      worksheet.getRow(rowIdx).height = 45; // 60px
      worksheet.mergeCells(`A${rowIdx}:${lastTopSectionCol}${rowIdx}`);
    };

    // Row 1: Logo row with dark background (full width)
    const logoRow = worksheet.getRow(1);
    logoRow.height = 45; // 60px
    logoRow.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" }, name: "Calibri" };
    logoRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };

    for (let col = 1; col <= topSectionColCount; col++) {
      const cell = worksheet.getCell(1, col);
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF09003D" } };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    }

    // Insert logo image if available
    if (logoBase64) {
      const base64Data = logoBase64.includes(",") ? logoBase64.split(",")[1] : logoBase64;
      const logoImgId = worksheet.workbook.addImage({ base64: base64Data, extension: "png" });

      const logoHeight = 40; // pixels
      const logoWidth = Math.round(logoHeight * logoAspectRatio);

      // Move the image to the right by a fixed number of pixels from the left edge of A1.
      // Tune this value to nudge the logo without recalculating its center.
      const columnAWidthPx = 360; // cell A approximate pixel width (matching width set earlier)
      const logoOffsetPx = 0;
      const colFraction = Math.max(0, logoOffsetPx / columnAWidthPx);

      // Small vertical fraction to keep image inside the logo row
      const rowFraction = 0;

      worksheet.addImage(logoImgId, {
        tl: { col: colFraction, row: rowFraction },
        ext: { width: logoWidth, height: logoHeight },
        editAs: "oneCell",
      });
    }

    // Row 2: Title row with branding (centered, unmerged)
    const titleCell = worksheet.getCell("A2");
    titleCell.value = title;
    titleCell.font = { bold: true, size: 13, color: { argb: "FF09003D" }, name: "Calibri" };
    titleCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    worksheet.getRow(2).height = 45; // 60px

    // Rows 3-9: Metadata (centered, full width)
    const topMetadataLines = metadataLines.slice(0, 7);
    let currentRow = 3;
    topMetadataLines.forEach((line) => {
      const metaCell = worksheet.getCell(`A${currentRow}`);
      metaCell.value = line;
      metaCell.font = { size: 9, color: { argb: "FF777777" }, name: "Calibri" };
      metaCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      worksheet.getRow(currentRow).height = 45; // 60px
      currentRow++;
    });

    // Row 10: Disclaimer (indented 4 from start of cell)
    const disclaimerRowIdx = 10;
    writeDisclaimerRow(disclaimerRowIdx);

    // Row 11: Explicitly empty row
    const spacingRow = worksheet.getRow(11);
    spacingRow.values = [];
    spacingRow.height = 45; // 60px

    // Header row starts at fixed row 12
    const headerRowIdx = 12;
    const headerRow = worksheet.getRow(headerRowIdx);
    headerRow.height = 45; // 60px
    // Paint header cells across the same width as the top section so styling matches
    for (let col = 1; col <= topSectionColCount; col++) {
      const cell = headerRow.getCell(col);
      // If we have a column label, set it; otherwise leave empty but styled
      if (col <= columns.length) {
        cell.value = columns[col - 1];
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, name: "Calibri" };
      }
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF09003D" } };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    }

    // Set column widths - convert 360px to ExcelJS character units (360px / 7 ≈ 51 units)
    columns.forEach((column, index) => {
      // Column A and C get 360px width (51 character units), others get dynamic width
      if (index === 0 || index === 2) {
        worksheet.getColumn(index + 1).width = 51;
      } else if (column === "url") {
        worksheet.getColumn(index + 1).width = 80;
      } else {
        const width = Math.max(column.length + 4, 16);
        worksheet.getColumn(index + 1).width = width;
      }
    });

    // Add data rows with alternating background and consistent height
    let dataRowIdx = headerRowIdx + 1;
    rows.forEach((row, rowIndex) => {
      const dataRow = worksheet.getRow(dataRowIdx);
      dataRow.font = { name: "Calibri", size: 10 };
      dataRow.height = 45; // 60px

      // Populate cells explicitly up to topSectionColCount so styling and fills
      // only cover the same width as the top section.
      for (let col = 1; col <= topSectionColCount; col++) {
        const cell = dataRow.getCell(col);
        if (col <= columns.length) {
          cell.value = normalizeExportValue(row[columns[col - 1]]);
        } else {
          cell.value = null;
        }

        // Alternate row background color for readability (apply per cell)
        if (rowIndex % 2 === 1) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F5F7" } };
        }

        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      }

      dataRowIdx++;
    });

    // Bottom disclaimer with same style and positioning as top.
    const bottomSpacingRow = worksheet.getRow(dataRowIdx);
    bottomSpacingRow.values = [];
    bottomSpacingRow.height = 45; // 60px
    writeDisclaimerRow(dataRowIdx + 1);
  };

  const downloadCurrentTabOrAllTabs = async (scopeLabel, fileType) => {
    // Map activeTab display name to export key, or get all tabs for current monitoring view
    let tabsToExport;
    if (scopeLabel === "all-tabs") {
      tabsToExport = getVisibleTabsForExport();
    } else {
      // For current tab, map the display name to export key
      const exportKey = mapTabNameToExportKey(activeTab);
      tabsToExport = [exportKey];
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Code-Cube.io";

    if (fileType === "xlsx") {
      tabsToExport.forEach((tabKey) => {
        const rows = getExportTabRows(tabKey);
        const exportRows = buildExportRows(tabKey, rows, scopeLabel);
        const columns = getExportTabColumns(tabKey, exportRows);
        const metadataLines = buildExportMetadataLines(scopeLabel, tabKey);
        const sheetName = (exportTabDisplayLabels[tabKey] || tabKey)
          .replace(/[:\\/?*\[\]]/g, "")
          .slice(0, 31);
        const worksheet = workbook.addWorksheet(sheetName);
        writeWorksheet(
          worksheet,
          exportTabDisplayLabels[tabKey] || tabKey,
          metadataLines,
          columns,
          exportRows,
        );
      });

      const buffer = await workbook.xlsx.writeBuffer();
      downloadBlob(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${getExportFileStem(scopeLabel)}.xlsx`,
      );
      return;
    }

    const csvRows = [];

    tabsToExport.forEach((tabKey, tabIndex) => {
      const rows = getExportTabRows(tabKey);
      const metadataLines = buildExportMetadataLines(scopeLabel, tabKey);
      
      // Add tab section header if all-tabs export
      if (scopeLabel === "all-tabs" && tabIndex > 0) {
        csvRows.push([]);
        csvRows.push([]);
      }
      
      metadataLines.forEach((line) => csvRows.push([line]));
      csvRows.push([]);

      const columns = getExportTabColumns(tabKey, rows);
      const csvColumns = ["monitoring_view", "tab", ...columns];
      csvRows.push(csvColumns);

      const exportRows = buildExportRows(tabKey, rows, scopeLabel);
      exportRows.forEach((row) => {
        const csvRow = csvColumns.map((column) => normalizeExportValue(row[column]));
        csvRows.push(csvRow);
      });
    });

    const csvContent = `\ufeff${csvRows.map((row) => buildCsvLine(row)).join("\r\n")}`;
    downloadBlob(
      new Blob([csvContent], { type: "text/csv;charset=utf-8;" }),
      `${getExportFileStem(scopeLabel)}.csv`,
    );
  };

  const getExportFileStem = (scopeLabel) => {
    const tabLabel = scopeLabel === "all-tabs" ? "all-tabs" : (exportTabLabels[activeTab] || activeTab);
    const scopePart =
      scopeLabel === "all-tabs" ? "all-tabs" : `current-tab-${tabLabel}`;
    return `tag-monitor-${monitoringView}-${scopePart}-${dayjs().format(
      "YYYY-MM-DD",
    )}`;
  };

  const exportMenuItems = [
    {
      key: "current-tab",
      label: "Current tab",
      children: [
        {
          key: "current-tab-xlsx",
          label: "Download Excel",
          icon: <Download />,
          onClick: () => downloadCurrentTabOrAllTabs("current-tab", "xlsx"),
        },
        {
          key: "current-tab-csv",
          label: "Download CSV",
          icon: <Download />,
          onClick: () => downloadCurrentTabOrAllTabs("current-tab", "csv"),
        },
      ],  
    },
    {
      key: "all-tabs",
      label: "All tabs",
      children: [
        {
          key: "all-tabs-xlsx",
          label: "Download Excel",
          icon: <Download />,
          onClick: () => downloadCurrentTabOrAllTabs("all-tabs", "xlsx"),
        },
        {
          key: "all-tabs-csv",
          label: "Download CSV",
          icon: <Download />,
          onClick: () => downloadCurrentTabOrAllTabs("all-tabs", "csv"),
        },
      ],
    },
  ];

  const rowClickFunction = (event) => {
    /*
            This function filters the data for the selected tag from the 
            table entries and updates the charts based on the selected tag 
            on the chart.
        */
    // If event is null (deselect): SHOW ALL!
    if (!event) {
      switch (tabType) {
        case "errors_client":
          setClientErrorsData(
            renderLineChart({
              dataArr: filterExcludedFromData(freshData.error_query_client),
              aggrParam: "total_fails_percent",
              setLabelFunc: setClientErrorsLabels,
              chartOptionType: "error-monitoring",
              chartOptionFunc: setClientErrorsOptions,
              groupByOption: clientErrorsGroupOption,
            }),
          );
          break;
        case "errors_server":
          setServerErrorsData(
            renderLineChart({
              dataArr: filterExcludedFromData(freshData.error_query_server),
              aggrParam: "total_fails_percent",
              setLabelFunc: setServerErrorsLabels,
              chartOptionType: "error-monitoring",
              chartOptionFunc: setServerErrorsOptions,
              groupByOption: serverErrorsGroupOption,
            }),
          );
          break;
        case "tag_volume_client":
          if (anomalyToggleOn) {
            setAnomalyClientData(
              renderLineChart({
                dataArr: freshData.anomaly_data_client,
                aggrParam: "tagfiredcountlog",
                setLabelFunc: setAnomalyClientLabels,
                chartOptionFunc: setAnomalyClientOptions,
                chartOptionType: "anomaly-monitoring",
                chartElementsNum: chartEntriesNum,
                groupByOption: clientTagAnomalyGroupOption,
                aggregateAllTags: true,
              }),
            );
          } else {
            setTagVolTotalClientData(
              renderLineChart({
                dataArr: filterExcludedFromData(freshData.tag_vol_total_calls_client),
                aggrParam: "total_calls",
                setLabelFunc: setTagVolTotalClientLabels,
                chartOptionType: "tag-volume",
                chartOptionFunc: setTagVolTotalClientOptions,
                groupByOption: clientTagVolumeGroupOption,
              }),
            );
          }
          break;
        case "tag_volume_server":
          if (anomalyToggleOn) {
            setAnomalyServerData(
              renderLineChart({
                dataArr: freshData.anomaly_data_server,
                aggrParam: "tagfiredcountlog",
                setLabelFunc: setAnomalyServerLabels,
                chartOptionFunc: setAnomalyServerOptions,
                chartOptionType: "anomaly-monitoring",
                chartElementsNum: chartEntriesNum,
                groupByOption: serverTagAnomalyGroupOption,
                aggregateAllTags: true,
              }),
            );
          } else {
            setTagVolTotalServerData(
              renderLineChart({
                dataArr: filterExcludedFromData(freshData.tag_vol_total_calls_server),
                aggrParam: "total_calls",
                setLabelFunc: setTagVolTotalServerLabels,
                chartOptionType: "tag-volume",
                chartOptionFunc: setTagVolTotalServerOptions,
                groupByOption: serverTagVolumeGroupOption,
              }),
            );
          }
          break;
        case "tag_anomaly_client":
          setAnomalyClientData(
            renderLineChart({
              dataArr: freshData.anomaly_data_client,
              aggrParam: "tagfiredcountlog",
              setLabelFunc: setAnomalyClientLabels,
              chartOptionFunc: setAnomalyClientOptions,
              chartOptionType: "anomaly-monitoring",
              chartElementsNum: chartEntriesNum,
              groupByOption: clientTagAnomalyGroupOption,
              aggregateAllTags: true,
            }),
          );
          break;
        case "tag_anomaly_server":
          setAnomalyServerData(
            renderLineChart({
              dataArr: freshData.anomaly_data_server,
              aggrParam: "tagfiredcountlog",
              setLabelFunc: setAnomalyServerLabels,
              chartOptionFunc: setAnomalyServerOptions,
              chartOptionType: "anomaly-monitoring",
              chartElementsNum: chartEntriesNum,
              groupByOption: serverTagAnomalyGroupOption,
              aggregateAllTags: true,
            }),
          );
          break;
        case "site_performance":
          setSitePerfPerTagData(
            renderLineChart({
              dataArr: filterExcludedFromData(freshData.site_performance_per_tag),
              aggrParam: "avg_ex_time",
              setLabelFunc: setSitePerfPerTagLabels,
              chartOptionType: "site-performance",
              chartOptionFunc: setSitePerfPerTagOptions,
              groupByOption: sitePerfGroupOption,
            }),
          );
          setSitePerfPerTriggerData(
            renderLineChart({
              dataArr: filterExcludedFromData(freshData.site_performance_per_trigger),
              aggrParam: "avg_ex_time",
              setLabelFunc: setSitePerfPerTriggerLabels,
              chartOptionType: "site-performance",
              chartOptionFunc: setSitePerfPerTriggerOptions,
              groupByOption: sitePerfGroupOption,
            }),
          );
          break;
        case "consent_monitoring":
          setConsentMonitoringData(freshData.consent_monitoring || []);
          break;
        default:
      }
      return; // Stop here!
    }

    // Pick the original rows if present
    const baseRows =
      event.sourceRows && event.sourceRows.length > 0
        ? event.sourceRows
        : [event];

    let filteredData, chartData;

    // Anomaly rows use tagid/containerid/hostname; everything else uses tag_id/container_id/event_name
    const isAnomalyTab =
      tabType === "tag_anomaly_client" ||
      tabType === "tag_anomaly_server" ||
      ((tabType === "tag_volume_client" || tabType === "tag_volume_server") &&
        anomalyToggleOn);

    const uniqueKeys = new Set(
      baseRows.flatMap((r) => {
        if (isAnomalyTab) {
          return `${r.tagid || ""}|||${r.containerid || ""}|||${r.hostname || ""}`;
        }
        // Key with and without version, so chart data with container_version also filters on it.
        const key = `${r.tag_id || ""}|||${r.container_id || ""}|||${r.event_name || ""}`;
        return [key, `${key}|||${r.container_version || ""}`];
      }),
    );

    const processData = (
      data,
      setDataFunc,
      aggrParam,
      setLabelFunc,
      chartType,
      groupByOption,
    ) => {
      // Show all underlying rows for the selected aggregate
      if (chartType === "anomaly") {
        filteredData = data?.filter((item) =>
          uniqueKeys.has(
            `${item.tagid || ""}|||${item.containerid || ""}|||${item.hostname || ""}`,
          ),
        );
      } else {
        filteredData = data?.filter((item) =>
          uniqueKeys.has(
            `${item.tag_id || ""}|||${item.container_id || ""}|||${item.event_name || ""}` +
              ("container_version" in item ? `|||${item.container_version || ""}` : ""),
          ),
        );
      }

      let chartOptionFunc;
      let chartOptionType;

      switch (chartType) {
        case "client":
          chartOptionFunc =
            aggrParam === "total_fails_percent"
              ? setClientErrorsOptions
              : setTagVolTotalClientOptions;
          chartOptionType =
            aggrParam === "total_fails_percent"
              ? "error-monitoring"
              : "tag-volume";
          break;
        case "anomaly":
          chartOptionFunc =
            tabType === "tag_anomaly_server" ||
            (tabType === "tag_volume_server" && anomalyToggleOn)
              ? setAnomalyServerOptions
              : setAnomalyClientOptions;
          setDataFunc(
            renderLineChart({
              dataArr: filteredData,
              aggrParam: "tagfiredcountlog",
              setLabelFunc: setLabelFunc,
              chartOptionType: "anomaly-monitoring",
              chartOptionFunc: chartOptionFunc,
              groupByOption: groupByOption,
              aggregateAllTags: false,
            }),
          );
          return;
        case "site-performance":
          if (sitePerfGroupOption === "tag_name") {
            chartOptionFunc = setSitePerfPerTagOptions;
            chartOptionType = "site-performance";
            setDataFunc(
              renderLineChart({
                dataArr: filteredData,
                aggrParam: aggrParam,
                setLabelFunc: setSitePerfPerTagLabels,
                chartOptionType: chartOptionType,
                chartOptionFunc: chartOptionFunc,
                groupByOption: groupByOption,
              }),
            );
          } else {
            chartOptionFunc = setSitePerfPerTriggerOptions;
            chartOptionType = "site-performance";
            setDataFunc(
              renderLineChart({
                dataArr: filteredData,
                aggrParam: aggrParam,
                setLabelFunc: setSitePerfPerTriggerLabels,
                chartOptionType: chartOptionType,
                chartOptionFunc: chartOptionFunc,
                groupByOption: groupByOption,
              }),
            );
          }
          return;
        default:
          chartOptionFunc =
            aggrParam === "total_fails_percent"
              ? setServerErrorsOptions
              : setTagVolTotalServerOptions;
          chartOptionType =
            aggrParam === "total_fails_percent"
              ? "error-monitoring"
              : "tag-volume";
      }

      // update the chart with the filtered data for the selected tag
      setDataFunc(
        renderLineChart({
          dataArr: filteredData,
          aggrParam: aggrParam,
          setLabelFunc: setLabelFunc,
          chartOptionType: chartOptionType,
          chartOptionFunc: chartOptionFunc,
          groupByOption: groupByOption,
        }),
      );
    };

    switch (tabType) {
      case "errors_client":
        chartData = filterExcludedFromData(refresh
          ? initialData.error_query_client
          : freshData.error_query_client);
        processData(
          chartData,
          setClientErrorsData,
          "total_fails_percent",
          setClientErrorsLabels,
          "client",
          clientErrorsGroupOption,
        );
        break;

      case "errors_server":
        chartData = filterExcludedFromData(refresh
          ? initialData.error_query_server
          : freshData.error_query_server);
        processData(
          chartData,
          setServerErrorsData,
          "total_fails_percent",
          setServerErrorsLabels,
          "server",
          serverErrorsGroupOption,
        );
        break;

      case "tag_volume_client":
        if (anomalyToggleOn) {
          chartData = refresh
            ? initialData.anomaly_data_client
            : freshData.anomaly_data_client;
          processData(
            chartData,
            setAnomalyClientData,
            "tagfiredcountlog",
            setAnomalyClientLabels,
            "anomaly",
            clientTagAnomalyGroupOption,
          );
        } else {
          chartData = filterExcludedFromData(refresh
            ? initialData.tag_vol_total_calls_client
            : freshData.tag_vol_total_calls_client);
          processData(
            chartData,
            setTagVolTotalClientData,
            "total_calls",
            setTagVolTotalClientLabels,
            "client",
            clientTagVolumeGroupOption,
          );
        }
        break;

      case "tag_volume_server":
        if (anomalyToggleOn) {
          chartData = refresh
            ? initialData.anomaly_data_server
            : freshData.anomaly_data_server;
          processData(
            chartData,
            setAnomalyServerData,
            "tagfiredcountlog",
            setAnomalyServerLabels,
            "anomaly",
            serverTagAnomalyGroupOption,
          );
        } else {
          chartData = filterExcludedFromData(refresh
            ? initialData.tag_vol_total_calls_server
            : freshData.tag_vol_total_calls_server);
          processData(
            chartData,
            setTagVolTotalServerData,
            "total_calls",
            setTagVolTotalServerLabels,
            "server",
            serverTagVolumeGroupOption,
          );
        }
        break;

      case "tag_anomaly_client":
        chartData = refresh ? initialData.anomaly_data_client : freshData.anomaly_data_client;
        processData(chartData, setAnomalyClientData, "tagfiredcountlog", setAnomalyClientLabels, "anomaly", clientTagAnomalyGroupOption);
        break;
    
      case "tag_anomaly_server":
          chartData = refresh ? initialData.anomaly_data_server : freshData.anomaly_data_server;
          processData(chartData, setAnomalyServerData, "tagfiredcountlog", setAnomalyServerLabels, "anomaly", serverTagAnomalyGroupOption);
          break;

      case "site_performance":
        chartData = filterExcludedFromData(refresh
          ? initialData.site_performance_per_tag
          : freshData.site_performance_per_tag);
        processData(
          chartData,
          setSitePerfPerTagData,
          "avg_ex_time",
          setSitePerfPerTagLabels,
          "site-performance",
          sitePerfGroupOption,
        );
        chartData = filterExcludedFromData(refresh
          ? initialData.site_performance_per_trigger
          : freshData.site_performance_per_trigger);
        processData(
          chartData,
          setSitePerfPerTriggerData,
          "avg_ex_time",
          setSitePerfPerTriggerLabels,
          "site-performance",
          sitePerfGroupOption,
        );
        break;

      case "consent_monitoring": {
        // Match the clicked row exactly; the table shows "-" for missing values.
        const consentData = freshData.consent_monitoring || [];
        const filteredConsentData = consentData.filter(
          (item) =>
            (item.tag_name || "-") === event.tag_name &&
            item.consent_name === event.consent_name &&
            (item.container_id || "-") === event.container_id &&
            (item.hostname || "-") === event.hostname &&
            (item.event_name || "-") === event.event_name,
        );
        setConsentMonitoringData(filteredConsentData);
        break;
      }

      default:
    }
  };

  const updateFiltersData = (data) => {
    /**
            *This function updates the data for the values
            inside the filter dropdowns. 
        */

    const filtersClientSide = data?.context?.filters_client_side || [];
    const filtersServerSide = data?.context?.filters_server_side || [];
    setDomainsConfigured(data?.context?.domains_configured ?? null);
    setConfiguredDomains(data?.context?.configured_domains || []);
    const excludedTagsClient =
      data.context.tags_monitoring_exclude_client || [];
    const excludedTagsServer =
      data.context.tags_monitoring_exclude_server || [];

    setFiltersClientSide(filtersClientSide);
    setFiltersServerSide(filtersServerSide);
    setExcludedTagsClient(excludedTagsClient);
    setExcludedTagsServer(excludedTagsServer);

    switch (filtersType) {
      case "client":
        setFiltersData(filtersClientSide);
        setTagsList(filtersClientSide || []);

        break;
      case "server":
        setFiltersData(filtersServerSide);
        setTagsList(filtersServerSide || []);
        break;
      default:
        setFiltersData([]);
    }
  };

  const updateChartData = (data, chart, setDataFunc) => {
    /**
     * This function updates either the initialData or
     * freshData variables.
     */
    switch (chart) {
      case "errors":
      
      // console.debug("Updating errors_client data...", data?.context?.error_query_client);
        // console.debug("Updating errors_server data...", data?.context?.error_query_server);
        setDataFunc((prev) => ({
          ...prev,
          ["error_query_client"]: data?.context?.error_query_client,
          ["error_query_server"]: data?.context?.error_query_server,
        }));
        break;

      case "tag-vol":
        // console.debug("Updating tag_vol_total_calls_client data...", data?.context?.tag_vol_total_calls_client);
        setDataFunc((prev) => ({
          ...prev,
          ["tag_vol_query_client"]: data?.context?.tag_vol_query_client,
          ["tag_vol_query_server"]: data?.context?.tag_vol_query_server,
          ["tag_vol_total_calls_client"]:
            data?.context?.tag_vol_total_calls_client,
          ["tag_vol_total_calls_server"]:
            data?.context?.tag_vol_total_calls_server,
        }));
        break;

      case "tag-anomaly":
        setDataFunc((prev) => ({
            ...prev,
            ["anomaly_data_client"]: data?.context?.anomaly_data_client,
            ["anomaly_data_server"]: data?.context?.anomaly_data_server,
        }));
        break;

      case "site-performance":
        setDataFunc((prev) => ({
          ...prev,
          ["site_performance_per_tag"]: data?.context?.site_performance_per_tag,
          ["site_performance_per_trigger"]:
            data?.context?.site_performance_per_trigger,
        }));
        break;

      case "consent-monitoring":
        setDataFunc((prev) => ({
          ...prev,
          ["consent_monitoring"]: data?.context?.consent_monitoring,
        }));
        break;

      case "cloud-run":
        // console.debug("Updating cloud_run data...", data?.context?.cloud_run);
        setDataFunc((prev) => ({
          ...prev,
          ["cloud_run"]: data?.context?.cloud_run,
        }));
        break;

      default:
        break;
    }
  };

  const updateData = (data, chart) => {
    if (companyID === companyId) {
      if (!initialLoad) {
        updateChartData(data, chart, setInitialData);
      }
    } else {
      setClientTimeStamp("Europe/Amsterdam");
      setDifferenceOneDayOrOneHour(null);
      updateChartData(data, chart, setInitialData);
    }

    updateChartData(data, chart, setFreshData);
    updateCharts(data?.context, chart);
  };

  //chart loading states
  const [errorsChartsLoading, setErrorsChartsLoading] = useState(true);
  const [tagVolChartsLoading, setTagVolChartsLoading] = useState(true);
  const [sitePerfChartsLoading, setSitePerfChartsLoading] = useState(true);
  const [consentMonitoringLoading, setConsentMonitoringLoading] =
    useState(true);
  const [cloudRunLoading, setCloudRunLoading] = useState(true);
  const [mainPageLoading, setMainPageLoading] = useState(false);


  const fetchTagDiagnostics = async (reqObj) => {
    // Early access: staff and the pilot list only. Without diagnoses the
    // column stays hidden and the drawer can never open, so this one guard
    // gates the whole feature.
    if (!canAccessTagDiagnostics()) {
      setDiagnosticsByTag({});
      return;
    }

    try {
      const res = await DashboardService.getTagMonitorDashboard(
        reqObj,
        "tag-diagnostics",
      );
      const response = await res.json();
      const rows = response?.context?.tag_diagnostics || [];

      const lookup = {};
      for (const row of rows) {
        lookup[diagnosisKey(row.tag_id, row.container_id)] = row;
      }
      setDiagnosticsByTag(lookup);
    } catch (error) {
      // A missing diagnosis must never affect the error table.
      setDiagnosticsByTag({});
    }
  };

  // Deep link: ?tag_id=..&container_id=.. opens that tag's drawer once the
  // diagnoses have loaded, so a notification link can land on it directly.
  const deepLinkHandledRef = useRef(false);

  useEffect(() => {
    if (deepLinkHandledRef.current) return;

    const deepLinkTagId = searchParams.get("tag_id");
    const deepLinkContainerId = searchParams.get("container_id");
    if (!deepLinkTagId || !deepLinkContainerId) return;

    const key = diagnosisKey(deepLinkTagId, deepLinkContainerId);
    const diagnosis = diagnosticsByTag[key];
    if (diagnosis) {
      setOpenDiagnosis(diagnosis);
      setDiagnosisDrawerOpen(true);
      deepLinkHandledRef.current = true;
    }
  }, [diagnosticsByTag, searchParams]);

  const getChartsData = async (reqObj) => {
    const chartsType = [
      "filters",
      "errors",
      "tag-vol",
      "tag-anomaly",
      "site-performance",
      "consent-monitoring",
      "cloud-run",
    ];
    let filtersCompleted,
      errorsCompleted,
      tagVolCompleted,
      tagAnomalyCompleted,
      SitePerfComplleted,
      consentMonitoringCompleted,
      cloudRunCompleted = false;

    chartsType.forEach(async (chart) => {
      let loadingChart;

      switch (chart) {
        case "errors":
          loadingChart = setErrorsChartsLoading;
          errorsCompleted = true;
          break;
        case "tag-vol":
          loadingChart = setTagVolChartsLoading;
          tagVolCompleted = true;
          break;
        case "tag-anomaly":
          loadingChart = setAnomalyChartsLoading;
          tagAnomalyCompleted = true;
          break;
        case "site-performance":
          loadingChart = setSitePerfChartsLoading;
          SitePerfComplleted = true;
          break;
        case "consent-monitoring":
          loadingChart = setConsentMonitoringLoading;
          consentMonitoringCompleted = true;
          break;
        case "cloud-run":
          loadingChart = setCloudRunLoading;
          cloudRunCompleted = true;
          break;
        default:
          loadingChart = setErrorsChartsLoading;
      }

      loadingChart(true);

      const res = await DashboardService.getTagMonitorDashboard(reqObj, chart);
      const response = await res.json();

      const dataset_exists = response?.context?.dataset_exists;
      const BQClientTimestamp = response?.context?.BQ_client_timestamp;
      setClientTimeStamp(BQClientTimestamp);

      if (dataset_exists) {
        if (chart === "filters") {
          updateFiltersData(response);

          if (initialLoad || companyID !== companyId) {
            setInitialFilters(response);
            setInitialSnapshotsList(() => response?.context?.templates);
          } else {
            if (!initialLoad) {
              setInitialFilters(response);
            }
          }
          filtersCompleted = true;
        } else {
          updateData(response, chart);
          setInitialLoad(true);
          setCompanyID(companyId);
          setDatasetExists(true);
          loadingChart(false);
        }
      } else {
        setDatasetExists(false);
      }

      setMainPageLoading(false);

      // enable the filters once all charts have been updated
      if (
        filtersCompleted &&
        errorsCompleted &&
        tagVolCompleted &&
        tagAnomalyCompleted &&
        SitePerfComplleted &&
        consentMonitoringCompleted &&
        cloudRunCompleted
      ) {
        setFiltersDisabled(false);
      }
    });
  };

  //specify the type (client/server) of the selected filters
  const [filtersDataType, setFiltersDataType] = useState(filtersType);

  const getFilterObject = () => {
    let setDifferenceOneDayOrOneHourParam,
      chosenContainerIdParam,
      chosenEventNameParam,
      chosenDomainParam,
      searchUrlValueParam,
      tagNamesList,
      requestStartDate,
      requestEndDate;

    // If the company is changed reset all the filters and empty tag list
    if (companyID !== companyId) {
      setDifferenceOneDayOrOneHour(null);
      tagNamesList = [];
      setClientTimeStamp("Europe/Amsterdam");
      setChosenContainerId([]);
      setChosenEventName([]);
      setChosenDomain([]);
      setSearchUrlValue("");
      setSelectedTags([]);

      setCurrentChosenContainerId([]);
      setCurrentChosenDomain([]);
      setChosenEventName([]);
      setCurrentSelectedTags([]);
      setCurrentSearchUrlValue("");

      // Create the reset date values locally
      const resetStartDate = dayjs().add(-7, "d");
      const resetEndDate = dayjs();
      
      // Set the state with the reset values
      setStartDate(resetStartDate);
      setEndDate(resetEndDate);
      
      // Use the local variables for the request
      requestStartDate = resetStartDate;
      requestEndDate = resetEndDate;
      setDifferenceOneDayOrOneHourParam = null;
      chosenContainerIdParam = "";
      chosenEventNameParam = "";
      chosenDomainParam = "";
      searchUrlValueParam = "";
    } else {
      tagNamesList = selectedTags;
      requestStartDate = startDate;
      requestEndDate = endDate;
      setDifferenceOneDayOrOneHourParam = differenceOneDayOrOneHour;
      chosenContainerIdParam = chosenContainerId;
      chosenEventNameParam = chosenEventName;
      chosenDomainParam = chosenDomain;
      searchUrlValueParam = searchUrlValue;
      setCurrentChosenContainerId(chosenContainerId);
      setCurrentChosenDomain(chosenDomain);
      setCurrentChosenEventName(chosenEventName);
      setCurrentSearchUrlValue(searchUrlValue);
      setCurrentSelectedTags(selectedTags);
    }

    const reqObj = {
      companyID: companyId,
      startDate: requestStartDate.format(timestampFormat),
      endDate: requestEndDate.format(timestampFormat),
      chosenContainerId: chosenContainerIdParam,
      chosenEventName: chosenEventNameParam,
      chosenDomain: chosenDomainParam,
      searchUrlValue: searchUrlValueParam,
      tagNamesList: [...new Set(tagNamesList)],
      differenceOneDayOrOneHour: setDifferenceOneDayOrOneHourParam,
      filtersType: filtersType,
      tagsMonitoringExcludeClient: excludedTagsClient,
      tagsMonitoringExcludeServer: excludedTagsServer,
    };

    setFiltersDataType(filtersType);
    return reqObj;
  };

  const handleChartGroupOptionChange = (value, setValueFunction) => {
    if (chartGroupOptions.some((option) => option.value === value)) {
      setValueFunction(value);
    }
  };

  const chartGroupByOptions = chartGroupOptions.map((option) => ({
    label: option.label,
    value: option.value,
  }));

  const [refresh, setRefresh] = useState(false);

  // Reset all values for the filter
  const refreshFilters = () => {
    setChosenContainerId([]);
    setChosenEventName([]);
    setChosenDomain([]);
    setSearchUrlValue("");
    setSelectedTags([]);
    setDefaultDateRange();
    setDifferenceOneDayOrOneHour(null);
    setRefresh(true);
    updateFiltersData(initialFilters);
    setCurrentChosenContainerId([]);
    setCurrentChosenDomain([]);
    setCurrentChosenEventName([]);
    setCurrentSearchUrlValue("");
    setCurrentSelectedTags([]);
    setSnapshotsList(initialSnapshotsList);
  };

  useEffect(() => {
    if (refresh) {
      setDifferenceOneDayOrOneHour(null);
      updateCharts(initialData, "all");
      // Keep freshData in sync with the reset, or a later row-click drilldown
      // filters a stale subset and finds nothing.
      setFreshData((prev) => ({ ...prev, ...initialData }));
      setRefresh(false); // Reset refresh to false after updating charts
    }
  }, [refresh]);

  // Refreshing the page data on company change
  useEffect(() => {
    (async () => {
      if (companyID !== companyId && !datasetExists) {
        setMainPageLoading(true);
        setDefaultDateRange();
        setSnapshotsList([]);
      }

      setFiltersDisabled(true);
      setRefresh(false);

      // Send a request to check if the session is active
      const session = await DashboardService.checkSession();
      const sessionData = await session?.json();
      const sessionExists = sessionData?.session_exists;

      if (sessionExists) {
        const filterObject = getFilterObject();
        const respData = await getChartsData(filterObject);
        await fetchTagDiagnostics(filterObject);
      } else {
        localStorageService.clearLocalStorage();
        navigate("/login");
      }
    })();
  }, [companyId, updateLoadFlag]);

  useEffect(() => {
    // toggle the tag name dropdown values based on the filter type
    const data =
      filtersType === "server" ? filtersServerSide : filtersClientSide || [];
    const tagsDropdownData = generateDropdownContent(data);
    setTagsDropdownList(tagsDropdownData);
  }, [tabType, filtersType, updateLoadFlag]);

  // updates the values for filers
  const [tagOptions, setTagOptions] = useState([]);
  const [cidOptions, setCidOptions] = useState([]);
  const [hostnameOptions, setHostnameOptions] = useState([]);
  const [eventsOptions, seteEventsOptions] = useState([]);
  const filterOptionsEmptyMessage = domainsConfigured === false ? (
    <div>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={null}
      />
      <p  style={{
            display: "block",
            maxWidth: "180px",
            margin: "-8px auto 0",
            lineHeight: 1.4,
            textAlign: "center",}}> 
        Add your domains in
        <a href="/tag_monitor_config?open=general"> General settings </a>
        to use these filters.
      </p>
    </div>
  ) : undefined;

  // Helper function to filter data based on selected filters (excluding one filter type)
  const getFilteredData = (excludeFilter) => {
    let filtered = filtersData || [];

    // Filter by selected tags (if not excluding tags)
    if (excludeFilter !== "tags" && selectedTags.length > 0) {
      filtered = filtered.filter((item) =>
        selectedTags.includes(item.tag_name),
      );
    }

    // Filter by selected container IDs (if not excluding container)
    if (excludeFilter !== "container" && chosenContainerId.length > 0) {
      filtered = filtered.filter((item) =>
        chosenContainerId.includes(item.container_id),
      );
    }

    // Filter by selected domains (if not excluding domain). For clients with
    // path-suffixed entries (e.g. "hyundai.com/de"), item.market carries the
    // full configured entry; fall back to bare hostname otherwise. A bare host
    // that has markets beneath it (e.g. "hyundai.com" with "hyundai.com/de") is
    // the global catch-all and keeps all rows.
    if (excludeFilter !== "domain" && chosenDomain.length > 0) {
      const catchAll = chosenDomain.some(
        (d) =>
          !d.includes("/") &&
          (filtersData || []).some((it) => (it.market || "").startsWith(`${d}/`)),
      );
      if (!catchAll) {
        filtered = filtered.filter((item) =>
          chosenDomain.includes(item.market ?? item.hostname),
        );
      }
    }

    // Filter by selected event names (if not excluding event)
    if (excludeFilter !== "event" && chosenEventName.length > 0) {
      filtered = filtered.filter((item) =>
        chosenEventName.includes(item.event_name),
      );
    }

    return filtered;
  };

  // useEffect for updating all filter dropdowns with interdependent filtering
  useEffect(() => {
    // Get filtered data for each dropdown (excluding itself from the filter)
    const tagsFilteredData = getFilteredData("tags");
    const containerFilteredData = getFilteredData("container");
    const domainFilteredData = getFilteredData("domain");
    const eventFilteredData = getFilteredData("event");

    // Extract unique values for each filter
    const tags = Array.from(
      new Set(tagsFilteredData.map((elem) => elem?.tag_name)),
    ).filter(Boolean);
    setTagOptions(
      tags.map((elem) => ({
        label: elem,
        value: elem,
      })),
    );

    const containers = Array.from(
      new Set(containerFilteredData.map((elem) => elem?.container_id)),
    ).filter(Boolean);
    setCidOptions(
      containers.map((elem) => ({
        label: elem,
        value: elem,
      })),
    );

    const domains = Array.from(
      new Set([
        ...configuredDomains,
        ...domainFilteredData.map((elem) => elem?.market ?? elem?.hostname),
      ]),
    ).filter(Boolean);
    setHostnameOptions(
      domains.map((elem) => ({
        label: elem,
        value: elem,
      })),
    );

    const events = Array.from(
      new Set(eventFilteredData.map((elem) => elem?.event_name)),
    ).filter(Boolean);
    seteEventsOptions(
      events.map((elem) => ({
        label: elem,
        value: elem,
      })),
    );
  }, [
    filtersData,
    configuredDomains,
    selectedTags,
    chosenContainerId,
    chosenDomain,
    chosenEventName,
  ]);

  const arraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;

    for (let i = 0; i <= arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }

    return true;
  };

  const sortOptionsWithSelectedOnTop = (options, selectedValues) => {
    return options.sort((a, b) => {
      const isASelected = selectedValues.includes(a.value);
      const isBSelected = selectedValues.includes(b.value);

      // If both are selected or both are unselected, maintain original order
      if (isASelected === isBSelected) {
        // If both are selected, maintain the order as in selectedValues array
        if (isASelected) {
          return (
            selectedValues.indexOf(a.value) - selectedValues.indexOf(b.value)
          );
        }
        // If both are unselected, sort alphabetically
        const labelA = String(a?.label || "");
        const labelB = String(b?.label || "");
        return labelA.localeCompare(labelB);
      }

      // Put selected options at the top
      return isASelected ? -1 : 1;
    });
  };

  const handleFilterChange = (e, updateFunction) => updateFunction(e);

  const handleClear = (updateFunction) => {
    updateFunction([]);
    handleLoseFocus();
  };

  const handleLoseFocus = () => {
    const selectedTagsChanged = !arraysEqual(currentSelectedTags, selectedTags);
    const cidListChanged = !arraysEqual(
      currentChosenContainerId,
      chosenContainerId,
    );
    const domainListChanged = !arraysEqual(currentChosenDomain, chosenDomain);
    const eventNameListChanged = !arraysEqual(
      currentChosenEventName,
      chosenEventName,
    );

    const hasFilterChanged =
      selectedTagsChanged ||
      cidListChanged ||
      domainListChanged ||
      eventNameListChanged ||
      currentSearchUrlValue !== searchUrlValue;

    if (hasFilterChanged) {
      // Clear the other view's saved filters when user changes a filter
      const otherView = filtersType === "client" ? "server" : "client";
      setFiltersByView((prev) => ({
        ...prev,
        [otherView]: {
          tags: [],
          containerId: [],
          eventName: [],
          domain: [],
          searchUrl: "",
        },
      }));

      // Always refetch so the chosen date range is kept when the last filter is removed.
      setUpdateLoadFlag(!updateLoadFlag);
    }
  };

  useEffect(() => {
    setUpdateLoadFlag(!updateLoadFlag);
  }, [
    clientErrorsGroupOption,
    serverErrorsGroupOption,
    clientTagVolumeGroupOption,
    serverTagVolumeGroupOption,
    clientTagAnomalyGroupOption,
    serverTagAnomalyGroupOption,
    sitePerfGroupOption,
  ]);

  const handleEnterPress = (e) =>
    currentSearchUrlValue !== searchUrlValue
      ? setUpdateLoadFlag(!updateLoadFlag)
      : undefined;

  // Chart max entries can't be less than 2 because the values of the consent monitoring
  const chartEntriesNumOptions = [2, 5, 10, 15, 20, 25, 30];
  const handleChartEntriesNumChange = (value, setValueFunction) => {
    if (chartEntriesNumOptions.indexOf(value) > -1) setValueFunction(value);
  };
  const chartEntriesOptions = chartEntriesNumOptions.map((elem) => ({
    label: elem,
    value: elem,
  }));

  const [chartEntriesNum, setChartEntriesNum] = useState(
    chartEntriesNumOptions[2],
  );

  useEffect(() => {
    updateCharts(freshData, "all");
  }, [chartEntriesNum]);

  useEffect(() => {
    localStorage.setItem("tagmonitor_show_excluded_tags", String(showExcludedTags));
    if (Object.keys(freshData).length > 0) {
      updateCharts(freshData, "all");
    }
  }, [showExcludedTags]);

  useEffect(() => {
    if (!showExcludedTags && Object.keys(freshData).length > 0) {
      updateCharts(freshData, "all");
    }
  }, [excludedTagsClient, excludedTagsServer]);

  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const currentView = params.get("view") || "client";
  const noDataTitle =
    activeTab === "tag_volume" && anomalyToggleOn
      ? tableInfo[`taganomaly${monitoringView}`]?.title
      : tableTitle;

  const handleMonitoringViewChange = (value) => {
    if (value === "client" || value === "server") {
      params.set("view", value);
      navigate(`${location.pathname}?${params.toString()}`);
    }
  };

  const monitoringOptions = [
    { label: "client-side", value: "client" },
    { label: "server-side", value: "server" },
  ];

  return (
    <Content>
      {datasetExists ? (
        <Space direction="vertical" className="container-content" size="large">
          <Flex justify="flex-start" gap="middle" align="flex-start">
            <PageHeader categoryName="dashboard" pageName={`Tag Monitor`} />
            <Select
              value={currentView}
              className="monitor-switch"
              defaultValue={params.get("view") || "client"}
              style={{ minWidth: "8rem" }}
              options={monitoringOptions}
              onChange={handleMonitoringViewChange}
              dropdownRender={(menu) => (
                <div className="monitor-switch-dropdown">
                  {menu} {/* just wrap it, don’t modify it */}
                </div>
              )}
            />
          </Flex>

          {/* Download button moved below into the table area to match DLG placement */}

          <Tabs
            items={[
              {
                name: "error_monitoring",
                title: "error monitoring",
                onClickFunction: () =>
                  handleDashTypeChangeValue(
                    monitoringView,
                    `errors_${monitoringView}`,
                    "error_monitoring",
                  ),
              },
              {
                name: "tag_volume",
                title: "tag volume",
                onClickFunction: () =>
                  handleDashTypeChangeValue(
                    monitoringView,
                    `tag_volume_${monitoringView}`,
                    "tag_volume",
                  ),
              },
              ...(monitoringView === "client"
                ? [
                    {
                      name: "consent_monitoring",
                      title: "consent monitoring",
                      onClickFunction: () =>
                        handleDashTypeChangeValue(
                          monitoringView,
                          "consent_monitoring",
                          "consent_monitoring",
                        ),
                    },
                    {
                      name: "site_performance",
                      title: "site performance",
                      onClickFunction: () =>
                        handleDashTypeChangeValue(
                          monitoringView,
                          "site_performance",
                          "site_performance",
                        ),
                    },
                  ]
                : []),
              ...(monitoringView === "server"
                ? [
                    {
                      name: "cloud_run",
                      title: "cloud run",
                      onClickFunction: () =>
                        handleDashTypeChangeValue(
                          monitoringView,
                          "cloud_run",
                          "cloud_run",
                        ),
                    },
                  ]
                : []),
            ]}
            activeTab={activeTab}
          />

          <Col span={24}>
            <p className="dashboard-desc-area">
              {activeTab === 'tag_volume' && anomalyToggleOn
                ? tabsInfo[`taganomaly${monitoringView}`]?.description
                : chartDesc}
              {/* Only explain the Tag Diagnostics column when the table actually shows it. */}
              {activeTab === "error_monitoring" && monitoringView === "client" && hasAnyDiagnosis && (
                <>
                  {" "}
                  Under the column <strong>Tag Diagnostics</strong> you can view the
                  severity levels of the error. Click on a severity level and the{" "}
                  <strong>Tag Debug Assistant</strong> will open.
                </>
              )}
            </p>
          </Col>

          <Row className="filters-row">
            <Flex
              style={{ width: "100%", flexWrap: "wrap" }}
              gap="small"
              justify={"flex-start"}
              align={"center"}
            >
              <Space style={{ minWidth: "7rem" }} direction="vertical">
                <RangePicker
                  presets={rangePresets}
                  format={timestampFormat}
                  onChange={onRangeChange}
                  variant="filled"
                  placeholder="time & date"
                  defaultValue={[startDate, endDate]}
                  value={[startDate, endDate]}
                  separator={<ArrowRight />}
                />
              </Space>
              {activeTab !== "cloud_run" && (
                <>
                  <Space
                    style={{ flex: "1 1 7rem", minWidth: "7rem" }}
                    direction="vertical"
                  >
                    <Select
                      popupMatchSelectWidth={false}
                      mode="multiple"
                      style={{ width: "100%" }}
                      placeholder="tag name"
                      onChange={(e) => handleFilterChange(e, setSelectedTags)}
                      options={sortOptionsWithSelectedOnTop(
                        tagOptions,
                        selectedTags,
                      )}
                      showSearch={true}
                      maxTagCount={0}
                      value={selectedTags}
                      disabled={filtersDisabled}
                      notFoundContent={filterOptionsEmptyMessage}
                      onBlur={handleLoseFocus}
                      onClear={() => handleClear(setSelectedTags)}
                      variant="filled"
                      suffixIcon={<ChevronDown />}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      filterSort={(optionA, optionB) =>
                        sortOptionsWithSelectedOnTop(
                          [optionA, optionB],
                          selectedTags,
                        )[0] === optionA
                          ? -1
                          : 1
                      }
                      autoClearSearchValue={false}
                      searchValue={undefined}
                      popupClassName="custom-dropdown"
                    />
                  </Space>

                  {activeTab === "consent_monitoring" && (
                    <Space
                      style={{ flex: "1 1 8rem", minWidth: "8rem" }}
                      direction="vertical"
                    >
                      <Select
                        popupMatchSelectWidth={false}
                        mode="multiple"
                        style={{ width: "100%" }}
                        placeholder="consent type"
                        onChange={(e) =>
                          handleFilterChange(e, setChosenConsentType)
                        }
                        options={sortOptionsWithSelectedOnTop(
                          consentTypeOptions,
                          chosenConsentType,
                        )}
                        showSearch={true}
                        maxTagCount={0}
                        value={chosenConsentType}
                        disabled={filtersDisabled}
                        notFoundContent={filterOptionsEmptyMessage}
                        onBlur={handleLoseFocus}
                        onClear={() => handleClear(setChosenConsentType)}
                        variant="filled"
                        suffixIcon={<ChevronDown />}
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        filterSort={(optionA, optionB) =>
                          sortOptionsWithSelectedOnTop(
                            [optionA, optionB],
                            chosenConsentType,
                          )[0] === optionA
                            ? -1
                            : 1
                        }
                        autoClearSearchValue={false}
                        searchValue={undefined}
                        popupClassName="custom-dropdown"
                      />
                    </Space>
                  )}

                  {/* Anomaly model output is aggregated per tag/hour and has no
                      event_name column, so event-name filtering can't apply on
                      the anomaly view. Keep the control visible but disabled
                      there, with a tooltip explaining why. */}
                  <AntdTooltip
                    title={
                      anomalyToggleOn
                        ? "Event filtering isn't available for anomaly data"
                        : ""
                    }
                  >
                  <Space
                    style={{ flex: "1 1 8rem", minWidth: "8rem" }}
                    direction="vertical"
                  >
                    <Select
                      popupMatchSelectWidth={false}
                      mode="multiple"
                      style={{ width: "100%" }}
                      placeholder="event name"
                      onChange={(e) =>
                        handleFilterChange(e, setChosenEventName)
                      }
                      options={sortOptionsWithSelectedOnTop(
                        eventsOptions,
                        chosenEventName,
                      )}
                      showSearch={true}
                      maxTagCount={0}
                      value={chosenEventName}
                      disabled={filtersDisabled || anomalyToggleOn}
                      notFoundContent={filterOptionsEmptyMessage}
                      onBlur={handleLoseFocus}
                      onClear={() => handleClear(setChosenEventName)}
                      variant="filled"
                      suffixIcon={<ChevronDown />}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      filterSort={(optionA, optionB) =>
                        sortOptionsWithSelectedOnTop(
                          [optionA, optionB],
                          chosenEventName,
                        )[0] === optionA
                          ? -1
                          : 1
                      }
                      autoClearSearchValue={false}
                      searchValue={undefined}
                      popupClassName="custom-dropdown"
                    />
                  </Space>
                  </AntdTooltip>

                  <Space
                    style={{ flex: "1 1 7rem", minWidth: "7rem" }}
                    direction="vertical"
                  >
                    <Select
                      popupMatchSelectWidth={false}
                      mode="multiple"
                      style={{ width: "100%" }}
                      placeholder="container"
                      onChange={(e) =>
                        handleFilterChange(e, setChosenContainerId)
                      }
                      options={sortOptionsWithSelectedOnTop(
                        cidOptions,
                        chosenContainerId,
                      )}
                      showSearch={true}
                      maxTagCount={0}
                      value={chosenContainerId}
                      disabled={filtersDisabled}
                      notFoundContent={filterOptionsEmptyMessage}
                      onBlur={handleLoseFocus}
                      onClear={() => handleClear(setChosenContainerId)}
                      variant="filled"
                      suffixIcon={<ChevronDown />}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      filterSort={(optionA, optionB) =>
                        sortOptionsWithSelectedOnTop(
                          [optionA, optionB],
                          chosenContainerId,
                        )[0] === optionA
                          ? -1
                          : 1
                      }
                      autoClearSearchValue={false}
                      searchValue={undefined}
                      popupClassName="custom-dropdown"
                    />
                  </Space>

                  <Space
                    style={{ flex: "1 1 6rem", minWidth: "6rem" }}
                    direction="vertical"
                  >
                    <Select
                      popupMatchSelectWidth={false}
                      mode="multiple"
                      style={{ width: "100%" }}
                      placeholder="domain"
                      onChange={(e) => handleFilterChange(e, setChosenDomain)}
                      options={sortOptionsWithSelectedOnTop(
                        hostnameOptions,
                        chosenDomain,
                      )}
                      showSearch={true}
                      maxTagCount={0}
                      value={chosenDomain}
                      disabled={filtersDisabled}
                      notFoundContent={filterOptionsEmptyMessage}
                      onBlur={handleLoseFocus}
                      onClear={() => handleClear(setChosenDomain)}
                      variant="filled"
                      suffixIcon={<ChevronDown />}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      filterSort={(optionA, optionB) =>
                        sortOptionsWithSelectedOnTop(
                          [optionA, optionB],
                          chosenDomain,
                        )[0] === optionA
                          ? -1
                          : 1
                      }
                      autoClearSearchValue={false}
                      searchValue={undefined}
                      popupClassName="custom-dropdown"
                    />
                  </Space>

                  {/* <Space style={{ minWidth: '14rem' }} direction="vertical">
                                        <Input
                                            placeholder="https://www.code-cube.io"
                                            variant="filled"
                                            type="text"
                                            value={searchUrlValue || ""}
                                            onChange={handleSearchUrlValue}
                                            disabled={filtersDisabled}
                                            onPressEnter={handleEnterPress}
                                            allowClear={true}
                                            suffixIcon={<ChevronDown />}
                                        />
                                    </Space>*/}
                  {/* Replace the existing entries selector Space component with this one */}
                  {activeTab !== "consent_monitoring" && (
                    <Space className="filter" style={{ Width: "100%" }}>
                      <Select
                        placeholder="# of entries"
                        onChange={setChartEntriesNum}
                        options={chartEntriesOptions}
                        showSearch={false}
                        value={chartEntriesNum}
                        disabled={errorsChartsLoading || !chartsReady}
                        variant="filled"
                        suffixIcon={<ChevronDown />}
                      />
                    </Space>
                  )}
                  {/* Tag/event name switch - Only show for error monitoring and tag volume */}
                  {(activeTab === "error_monitoring" ||
                    activeTab === "tag_volume") && (
                    <Space className="filter" style={{ Width: "100%" }}>
                      <Segmented
                        options={chartGroupByOptions}
                        onChange={(value) => {
                          if (activeTab === "error_monitoring") {
                            handleChartGroupOptionChange(
                              value,
                              monitoringView === "client"
                              ? setClientErrorsGroupOption
                              : setServerErrorsGroupOption,
                            );
                          } else if (activeTab === "tag_volume") {
                            if (anomalyToggleOn) {
                              handleChartGroupOptionChange(
                                value,
                                monitoringView === "client"
                                  ? setClientTagAnomalyGroupOption
                                  : setServerTagAnomalyGroupOption,
                              );
                            } else {
                              handleChartGroupOptionChange(
                                value,
                                monitoringView === "client"
                                  ? setClientTagVolumeGroupOption
                                  : setServerTagVolumeGroupOption,
                              );
                            }
                          }
                        }}
                        value={
                          activeTab === "error_monitoring"
                            ? monitoringView === "client"
                              ? clientErrorsGroupOption
                              : serverErrorsGroupOption
                            : anomalyToggleOn
                              ? monitoringView === "client"
                                ? clientTagAnomalyGroupOption
                                : serverTagAnomalyGroupOption
                              : monitoringView === "client"
                                ? clientTagVolumeGroupOption
                                : serverTagVolumeGroupOption
                        }
                        />
                    </Space>
                  )}
                  {(activeTab === "error_monitoring" ||
                    activeTab === "tag_volume" ||
                    activeTab === "site_performance") &&
                    (excludedTagsClient.length > 0 || excludedTagsServer.length > 0) && (
                      <Space className="filter excluded-tags-toggle" align="center">
                        <Switch
                          checked={showExcludedTags}
                          onChange={(checked) => setShowExcludedTags(checked)}
                        />
                        <span className="excluded-tags-toggle-label">Show excluded tags</span>
                      </Space>
                    )}
                </>
              )}
            </Flex>
          </Row>
          <Col>
            {monitoringView === "client" ? (
              <>
                <div
                  className={`charts-area ${tabsVisibility["errors_client"] ? "visible" : ""}`}
                >
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ position: "relative", width: "100%" }}
                  >
                    <EmptyChart
                      dataName={noDataTitle}
                      noFiltersApplied={noFiltersApplied}
                      chartData={{
                        labels: clientErrorsLabels,
                        datasets: clientErrorsData,
                      }}
                    >
                      <Chart
                        type="line"
                        data={{
                          labels: clientErrorsLabels,
                          datasets: clientErrorsData,
                        }}
                        width={1539}
                        height={520}
                        options={clientErrorsOptions}
                        plugins={[customMarginPlugin]}
                        className="charts-area-canvas"
                        key={chartEntriesNum}
                      />
                    </EmptyChart>
                  </Space>
                  <Col span={24} className="table-text-area">
                    <span className="table-title-area">{tableTitle}</span>
                    <p className="table-desc-area">{tableDesc}</p>
                  </Col>
                  <div>
                    <LoadingScreen isActive={errorsChartsLoading} />
                    <HiddenColumnsTable
                      columns={clientErrorTableColumns}
                      dataSource={aggregatedClientErrorsTable} // Changed from clientErrorsTable
                      rowClickFunction={(e) => rowClickFunction(e)}
                      filterParam="tagname"
                      onVisibleColumnsChange={setVisibleErrorColumns} // Add this prop
                      scrollX={false}
                      highlightRowKey={
                        diagnosisDrawerOpen ? openDiagnosisRowKey : null
                      }
                      headerActions={
                        <Dropdown
                          menu={{ items: exportMenuItems }}
                          placement="bottomRight"
                        >
                          <div className="text-area-copy-button">
                            <Download size={18} />
                          </div>
                        </Dropdown>
                      }
                    />
                  </div>
                </div>
                <Space
                  className={`charts-area ${tabsVisibility["tag_volume_client"] ? "visible" : ""}`}
                >
                  {hasAnomalyAccess && (
                    <Flex
                      className="anomaly-toggle-area"
                      justify="flex-end"
                      align="center"
                      gap="small"
                      style={{ width: "100%" }}
                    >
                      {anomalyToggleOn && (
                        <>
                          <span className="anomaly-toggle-label">Anomaly Only</span>
                          <Switch
                            checked={anomalyOnlyFilter}
                            onChange={(checked) => setAnomalyOnlyFilter(checked)}
                            disabled={anomalyChartsLoading}
                          />
                        </>
                      )}
                      <span className="anomaly-toggle-label">Anomaly Detection</span>
                      <Switch
                        checked={anomalyToggleOn}
                        onChange={(checked) => setAnomalyToggleOn(checked)}
                        disabled={anomalyChartsLoading}
                      />
                    </Flex>
                  )}
                  <Space
                    direction="vertical"
                    size="large"
                    style={{ position: "relative", width: "100%" }}
                  >
                    {anomalyToggleOn ? (
                      <EmptyChart
                        dataName={noDataTitle}
                        noFiltersApplied={noFiltersApplied}
                        chartData={{
                          labels: anomalyClientLabels,
                          datasets: anomalyClientData,
                        }}
                      >
                        <Chart
                          type="line"
                          data={{
                            labels: anomalyClientLabels,
                            datasets: anomalyClientData,
                          }}
                          width={1539}
                          height={520}
                          options={anomalyClientOptions}
                          plugins={[customMarginPlugin]}
                          className="charts-area-canvas"
                          key={`anomaly-client-${chartEntriesNum}`}
                        />
                      </EmptyChart>
                    ) : (
                      <EmptyChart
                        dataName={noDataTitle}
                        noFiltersApplied={noFiltersApplied}
                        chartData={{
                          labels: tagVolTotalClientLabels,
                          datasets: tagVolTotalClientData,
                        }}
                      >
                        <Chart
                          type="line"
                          data={{
                            labels: tagVolTotalClientLabels,
                            datasets: tagVolTotalClientData,
                          }}
                          width={1539}
                          height={520}
                          options={tagVolTotalClientOptions}
                          plugins={[customMarginPlugin]}
                          className="charts-area-canvas"
                          key={chartEntriesNum}
                        />
                      </EmptyChart>
                    )}
                  </Space>
                  <Col
                    span={24}
                    className="table-text-area anomaly-detected-header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <span className="table-title-area">
                        {anomalyToggleOn
                          ? tableInfo["taganomalyclient"]?.title || "Detected Anomalies"
                          : tableTitle}
                      </span>
                      <p className="table-desc-area">
                        {anomalyToggleOn
                          ? tableInfo["taganomalyclient"]?.description || tableDesc
                          : tableDesc}
                      </p>
                    </div>
                    {anomalyToggleOn &&
                      aggregatedAnomalyClientTable.length > 0 &&
                      renderAnomalySeveritySummary(aggregatedAnomalyClientTable, 'client')}
                  </Col>
                  <div>
                    <LoadingScreen
                      isActive={anomalyToggleOn ? anomalyChartsLoading : tagVolChartsLoading}
                    />
                    <div
                      className="tm-table-header-actions"
                      ref={errorTableWrapperRef}
                      style={{ position: "relative" }}
                    >
                      <Dropdown
                        menu={{ items: exportMenuItems }}
                        placement="bottomRight"
                      >
                        <div
                          className="text-area-copy-button"
                          ref={downloadButtonRef}
                          style={{
                            position: "absolute",
                            right: "24px",
                            top: "16px",
                            zIndex: 5,
                          }}
                        >
                          <Download size={18} />
                        </div>
                      </Dropdown>
                    </div>
                    {anomalyToggleOn ? (
                      anomalyOnlyFilter && aggregatedAnomalyClientTable.length === 0 ? (
                        <div className="no-anomaly-message no-data-table-message">
                          No anomalies detected in the selected time range
                        </div>
                      ) : aggregatedAnomalyClientTable.length === 0 && !anomalyChartsLoading ? (
                        <div className="no-anomaly-message no-data-table-message">
                          <NoDataMessageContent
                            dataName={noDataTitle}
                            noFiltersApplied={noFiltersApplied}
                          />
                        </div>
                      ) : (
                        <>
                          <HiddenColumnsTable
                            key="anomaly-client"
                            columns={anomalyTableColumns}
                            dataSource={aggregatedAnomalyClientTable}
                            rowClickFunction={(e) => rowClickFunction(e)}
                            filterParam="tagname"
                            onVisibleColumnsChange={setVisibleAnomalyColumns}
                          />
                        </>
                      )
                    ) : (
                      <HiddenColumnsTable
                        key="tagvol-client"
                        columns={tagVolumeTableColumns}
                        dataSource={aggregatedTagVolClientData}
                        rowClickFunction={(e) => rowClickFunction(e)}
                        filterParam="tagname"
                        onVisibleColumnsChange={setVisibleTagVolumeColumns}
                      />
                    )}
                  </div>
                </Space>
              </>
            ) : (
              <>
                <Space
                  className={`charts-area ${tabsVisibility["errors_server"] ? "visible" : ""}`}
                >
                  <Space
                    direction="vertical"
                    size="large"
                    style={{ position: "relative", width: "100%" }}
                  >
                    <EmptyChart
                      dataName={noDataTitle}
                      noFiltersApplied={noFiltersApplied}
                      chartData={{
                        labels: serverErrorsLabels,
                        datasets: serverErrorsData,
                      }}
                    >
                      <Chart
                        type="line"
                        data={{
                          labels: serverErrorsLabels,
                          datasets: serverErrorsData,
                        }}
                        width={1539}
                        height={520}
                        options={serverErrorsOptions}
                        plugins={[customMarginPlugin]}
                        className="charts-area-canvas"
                        key={chartEntriesNum}
                      />
                    </EmptyChart>
                  </Space>
                  <Col span={24} className="table-text-area">
                    <span className="table-title-area">{tableTitle}</span>
                    <p className="table-desc-area">{tableDesc}</p>
                  </Col>
                  <div>
                    <LoadingScreen isActive={errorsChartsLoading} />
                    <div
                      className="tm-table-header-actions"
                      ref={errorTableWrapperRef}
                      style={{ position: "relative" }}
                    >
                      <Dropdown
                        menu={{ items: exportMenuItems }}
                        placement="bottomRight"
                      >
                        <div
                          className="text-area-copy-button"
                          ref={downloadButtonRef}
                          style={
                            downloadBtnPos.left !== null
                              ? { position: "absolute", left: `${downloadBtnPos.left}px`, top: `${downloadBtnPos.top}px`, zIndex: 5 }
                              : { position: "absolute", right: "24px", top: "16px", zIndex: 5 }
                          }
                        >
                          <Download size={18} />
                        </div>
                      </Dropdown>
                    </div>
                    <HiddenColumnsTable
                      columns={errorTableColumns}
                      dataSource={aggregatedServerErrorsTable} // Changed from serverErrorsTable
                      rowClickFunction={(e) => rowClickFunction(e)}
                      filterParam="tagname"
                      onVisibleColumnsChange={setVisibleErrorColumns} // Add this prop
                    />
                  </div>
                </Space>

                <Space
                  className={`charts-area ${tabsVisibility["tag_volume_server"] ? "visible" : ""}`}
                >
                  {hasAnomalyAccess && (
                    <Flex
                      className="anomaly-toggle-area"
                      justify="flex-end"
                      align="center"
                      gap="small"
                      style={{ width: "100%" }}
                    >
                      {anomalyToggleOn && (
                        <>
                          <span className="anomaly-toggle-label">Anomaly Only</span>
                          <Switch
                            checked={anomalyOnlyFilter}
                            onChange={(checked) => setAnomalyOnlyFilter(checked)}
                            disabled={anomalyChartsLoading}
                          />
                        </>
                      )}
                      {/* <span className="anomaly-toggle-label">Anomaly Detection</span>
                      <Switch
                        checked={anomalyToggleOn}
                        onChange={(checked) => setAnomalyToggleOn(checked)}
                        disabled={anomalyChartsLoading}
                      /> */}
                    </Flex>
                  )}
                  <Space
                    direction="vertical"
                    size="large"
                    style={{ position: "relative", width: "100%" }}
                  >
                    {anomalyToggleOn ? (
                      <EmptyChart
                        dataName={noDataTitle}
                        noFiltersApplied={noFiltersApplied}
                        chartData={{
                          labels: anomalyServerLabels,
                          datasets: anomalyServerData,
                        }}
                      >
                        <Chart
                          type="line"
                          data={{
                            labels: anomalyServerLabels,
                            datasets: anomalyServerData,
                          }}
                          width={1539}
                          height={520}
                          options={anomalyServerOptions}
                          plugins={[customMarginPlugin]}
                          className="charts-area-canvas"
                          key={`anomaly-server-${chartEntriesNum}`}
                        />
                      </EmptyChart>
                    ) : (
                      <EmptyChart
                        dataName={noDataTitle}
                        noFiltersApplied={noFiltersApplied}
                        chartData={{
                          labels: tagVolTotalServerLabels,
                          datasets: tagVolTotalServerData,
                        }}
                      >
                        <Chart
                          type="line"
                          data={{
                            labels: tagVolTotalServerLabels,
                            datasets: tagVolTotalServerData,
                          }}
                          width={1539}
                          height={520}
                          options={tagVolTotalServerOptions}
                          plugins={[customMarginPlugin]}
                          className="charts-area-canvas"
                          key={chartEntriesNum}
                        />
                      </EmptyChart>
                    )}
                  </Space>
                  <Col
                    span={24}
                    className="table-text-area anomaly-detected-header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <span className="table-title-area">
                        {anomalyToggleOn
                          ? tableInfo["taganomalyserver"]?.title || "Detected Anomalies"
                          : tableTitle}
                      </span>
                      <p className="table-desc-area">
                        {anomalyToggleOn
                          ? tableInfo["taganomalyserver"]?.description || tableDesc
                          : tableDesc}
                      </p>
                    </div>
                    {anomalyToggleOn &&
                      aggregatedAnomalyServerTable.length > 0 &&
                      renderAnomalySeveritySummary(aggregatedAnomalyServerTable, 'server')}
                  </Col>
                  <div>
                    <LoadingScreen
                      isActive={anomalyToggleOn ? anomalyChartsLoading : tagVolChartsLoading}
                    />
                    <div
                      className="tm-table-header-actions"
                      ref={errorTableWrapperRef}
                      style={{ position: "relative" }}
                    >
                      <Dropdown
                        menu={{ items: exportMenuItems }}
                        placement="bottomRight"
                      >
                        <div
                          className="text-area-copy-button"
                          ref={downloadButtonRef}
                          style={{
                            position: "absolute",
                            right: "24px",
                            top: "16px",
                            zIndex: 5,
                          }}
                        >
                          <Download size={18} />
                        </div>
                      </Dropdown>
                    </div>
                    {anomalyToggleOn ? (
                      anomalyOnlyFilter && aggregatedAnomalyServerTable.length === 0 ? (
                        <div className="no-anomaly-message">
                          No anomalies detected in the selected time range
                        </div>
                      ) : aggregatedAnomalyServerTable.length === 0 && !anomalyChartsLoading ? (
                        <div className="no-anomaly-message no-data-table-message">
                          <NoDataMessageContent
                            dataName={noDataTitle}
                            noFiltersApplied={noFiltersApplied}
                          />
                        </div>
                      ) : (
                        <>
                          <HiddenColumnsTable
                            key="anomaly-server"
                            columns={anomalyTableColumns}
                            dataSource={aggregatedAnomalyServerTable}
                            rowClickFunction={(e) => rowClickFunction(e)}
                            filterParam="tagname"
                            onVisibleColumnsChange={setVisibleAnomalyColumns}
                          />
                        </>
                      )
                    ) : (
                      <HiddenColumnsTable
                        key="tagvol-server"
                        columns={tagVolumeTableColumns}
                        dataSource={aggregatedTagVolServerData}
                        rowClickFunction={(e) => rowClickFunction(e)}
                        filterParam="tagname"
                        onVisibleColumnsChange={setVisibleTagVolumeColumns}
                      />
                    )}
                  </div>
                </Space>
              </>
            )}
            {/* consent monitoring chart */}
            <Space
              className={`charts-area ${tabsVisibility["consent_monitoring"] ? "visible" : ""}`}
            >
              <Flex
                className="consent-monitoring-charts"
                justify="space-between"
                wrap
              >
                <div className="consent-monitoring-chart">
                  <BarChart
                    data={filteredConsentMonitoringData}
                    transformChartData={consentMonitoring}
                    dataName={noDataTitle}
                    noFiltersApplied={noFiltersApplied}
                  />
                </div>

                <div className="consent-monitoring-chart">
                  <LineChart
                    data={filteredConsentMonitoringData}
                    width={600}
                    height={320}
                    dataName={noDataTitle}
                    noFiltersApplied={noFiltersApplied}
                  />
                </div>
              </Flex>
              <Col span={24} className="table-text-area">
                <span className="table-title-area">{tableTitle}</span>
                <p className="table-desc-area">{tableDesc}</p>
              </Col>
              <div>
                <LoadingScreen isActive={consentMonitoringLoading} />
                <div
                  className="tm-table-header-actions"
                  ref={errorTableWrapperRef}
                  style={{ position: "relative" }}
                >
                  <Dropdown
                    menu={{ items: exportMenuItems }}
                    placement="bottomRight"
                  >
                    <div
                      className="text-area-copy-button"
                      ref={downloadButtonRef}
                      style={{
                        position: "absolute",
                        right: "24px",
                        top: "16px",
                        zIndex: 5,
                      }}
                    >
                      <Download size={18} />
                    </div>
                  </Dropdown>
                </div>
                <HiddenColumnsTable
                  columns={consentMonitoringTableColumns}
                  dataSource={(aggregatedConsentMonitoringData || []).filter(
                    matchesConsentType,
                  )}
                  rowClickFunction={(e) => {
                    rowClickFunction(e);
                  }}
                  onVisibleColumnsChange={setVisibleConsentColumns}
                />
              </div>
            </Space>

            {/* end */}
            {/* cloud run table */}
            <Space
              className={`charts-area ${tabsVisibility["cloud_run"] ? "visible" : ""}`}
            >
              <LoadingScreen isActive={cloudRunLoading} />
              <Col span={24} className="table-text-area">
                <span className="table-title-area">{tableTitle}</span>
                <p className="table-desc-area">{tableDesc}</p>
              </Col>
              <div>
                <div
                  className="tm-table-header-actions"
                  ref={errorTableWrapperRef}
                  style={{ position: "relative" }}
                >
                  <Dropdown
                    menu={{ items: exportMenuItems }}
                    placement="bottomRight"
                  >
                    <div
                      className="text-area-copy-button"
                      ref={downloadButtonRef}
                      style={{
                        position: "absolute",
                        right: "24px",
                        top: "0",
                        zIndex: 5,
                      }}
                    >
                      <Download size={18} />
                    </div>
                  </Dropdown>
                </div>
                {cloudRunData?.length > 0 ? (
                  <Table
                    columns={cloudRunTableColumns}
                    tableData={cloudRunData}
                    rowClickFunction={handleSnapshotAreaUpdate}
                    includeIndexColumn={false}
                    showSubHeader={false}
                  />
                ) : (
                  <div className="no-anomaly-message no-data-table-message">
                    <NoDataMessageContent
                      dataName={noDataTitle}
                      noFiltersApplied={noFiltersApplied}
                    />
                  </div>
                )}
                <div
                  className={`mb-3 ${snapshotExpanded ? "slide-down" : "slide-up"}`}
                >
                  <div
                    className="row"
                    style={{ display: "flex", margin: 0, padding: 0 }}
                  >
                    <div className="elems-list col-12">
                      <h5>
                        <strong>Cloud Run Alert Details</strong>
                      </h5>
                      <div className="error-details-desc">
                        <div className="row">
                          <div className="col-6">
                            <div>
                              <strong>Date / Time:</strong>{" "}
                              {snapshotToReview?.formatted_timestamp}
                            </div>
                            <div>
                              <strong>Project ID:</strong>{" "}
                              {snapshotToReview?.project_id}
                            </div>
                            <div>
                              <strong>Project Name:</strong>{" "}
                              {snapshotToReview?.project_name}
                            </div>
                            <div>
                              <strong>Service Name:</strong>{" "}
                              {snapshotToReview?.service_name}
                            </div>
                          </div>
                          <div className="col-6">
                            <div>
                              <strong>Location:</strong>{" "}
                              {snapshotToReview?.location}
                            </div>
                            <div>
                              <strong>Observed Value:</strong>{" "}
                              {snapshotToReview?.observed_value}
                            </div>
                            <div>
                              <strong>Threshold Value:</strong>{" "}
                              {snapshotToReview?.threshold_value}
                            </div>
                            <div>
                              <strong>Condition:</strong>{" "}
                              {snapshotToReview?.condition_name}
                            </div>
                          </div>
                        </div>
                        <div className="row mt-3">
                          <div className="col-12">
                            <div>
                              <strong>Summary:</strong>
                            </div>
                            <p>{snapshotToReview?.summary}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Space>
            {/* end */}
            {/* site performance chart */}
            <Space
              className={`charts-area ${tabsVisibility["site_performance"] ? "visible" : ""}`}
            >
              <Space
                direction="vertical"
                size="large"
                style={{ position: "relative", width: "100%" }}
              >
                {sitePerfGroupOption === "tag_name" ? (
                  <EmptyChart
                    dataName={noDataTitle}
                    noFiltersApplied={noFiltersApplied}
                    chartData={{
                      labels: sitePerfPerTagLabels,
                      datasets: sitePerfPerTagData,
                    }}
                  >
                    <Chart
                      type="line"
                      data={{
                        labels: sitePerfPerTagLabels,
                        datasets: sitePerfPerTagData,
                      }}
                      width={1539}
                      height={520}
                      options={sitePerfPerTagOptions}
                      plugins={[customMarginPlugin]}
                      className="charts-area-canvas"
                      key={chartEntriesNum}
                    />
                  </EmptyChart>
                ) : (
                  <EmptyChart
                    dataName={noDataTitle}
                    noFiltersApplied={noFiltersApplied}
                    chartData={{
                      labels: sitePerfPerTriggerLabels,
                      datasets: sitePerfPerTriggerData,
                    }}
                  >
                    <Chart
                      type="line"
                      data={{
                        labels: sitePerfPerTriggerLabels,
                        datasets: sitePerfPerTriggerData,
                      }}
                      width={1539}
                      height={520}
                      options={sitePerfPerTriggerOptions}
                      plugins={[customMarginPlugin]}
                      className="charts-area-canvas"
                      key={chartEntriesNum}
                    />
                  </EmptyChart>
                )}
              </Space>
              <Col span={24} className="table-text-area">
                <span className="table-title-area">{tableTitle}</span>
                <p className="table-desc-area">{tableDesc}</p>
              </Col>
              <div>
                <LoadingScreen isActive={sitePerfChartsLoading} />
                <div
                  className="tm-table-header-actions"
                  ref={errorTableWrapperRef}
                  style={{ position: "relative" }}
                >
                  <Dropdown
                    menu={{ items: exportMenuItems }}
                    placement="bottomRight"
                  >
                    <div
                      className="text-area-copy-button"
                      ref={downloadButtonRef}
                      style={{
                        position: "absolute",
                        right: "24px",
                        top: "16px",
                        zIndex: 5,
                      }}
                    >
                      <Download size={18} />
                    </div>
                  </Dropdown>
                </div>
                {(sitePerfGroupOption === "tag_name"
                  ? sitePerfPerTagTable
                  : sitePerfPerTriggerTable
                )?.length > 0 ? (
                  <Table
                    columns={sitePerformanceTableColumns}
                    tableData={
                      sitePerfGroupOption === "tag_name"
                        ? sitePerfPerTagTable
                        : sitePerfPerTriggerTable
                    }
                    rowClickFunction={(e) => {
                      rowClickFunction(e);
                    }}
                    filterParam={
                      sitePerfGroupOption === "tag_name"
                        ? "tag_name"
                        : "event_name"
                    }
                  />
                ) : (
                  <div className="no-anomaly-message no-data-table-message">
                    <NoDataMessageContent
                      dataName={noDataTitle}
                      noFiltersApplied={noFiltersApplied}
                    />
                  </div>
                )}
              </div>
            </Space>
            {/* end */}
          </Col>
        </Space>
      ) : (
        <Content>
          <LoadingScreen isActive={mainPageLoading} />
          <Space
            direction="vertical"
            className="container-content"
            size="large"
          >
            <Flex justify="space-between" align="flex-start">
              <PageHeader categoryName="dashboard" pageName="Tag Monitor" />
            </Flex>

            <Row>
              <h5 className="header-letter-spacing">Dataset not found!</h5>
            </Row>
          </Space>
        </Content>
      )}

      <TagDiagnosticsDrawer
        diagnosis={openDiagnosis}
        open={diagnosisDrawerOpen}
        onClose={closeDiagnosisDrawer}
      />
    </Content>
  );
};
